import { Controller } from "@hotwired/stimulus"
import interact from "interactjs"
import { get, post } from "@rails/request.js"

class GhostManager {
  constructor(ownerController) {
    this.ownerController = ownerController;
    this.debug = ownerController?.debugValue ?? false;
  }

  getElementHierarchy(element) {
    if (!element) return ''

    const hierarchy = []
    let current = element

    // Walk up the DOM tree until we hit the body
    while (current && current !== document.body) {
      const id = current.id || current.dataset?.dndControllerId
      if (id) {
        hierarchy.unshift(id)
      }
      current = current.parentElement
    }

    return hierarchy.join('>')
  }

  log(...args) {
    if (this.ownerController && this.ownerController.debugValue) {
      const source = args[1]?.source || args[0]?.source
      const hierarchy = source ? this.getElementHierarchy(source) : ''
      const prefix = `GhostManager[${hierarchy}]:`;
      this.ownerController.debug(prefix, ...args);
    }
  }

  createGhost(source, rect, style) {
    // Create ghost (clone)
    const ghost = document.createElement('div')
    const ghostElement = source.cloneNode(true)
    ghost.appendChild(ghostElement)
    ghost.classList.add('dnd-ghost')

    // Create pill indicator
    const pill = document.createElement('div')
    pill.classList.add('dnd-ghost-pill')
    pill.textContent = 'No valid drop area'
    ghost.appendChild(pill)

    // Remove any existing transitions that might interfere
    ghost.classList.remove('transition-all', 'duration-200')

    // Copy computed styles except transitions
    const computed = window.getComputedStyle(source)
    Array.from(computed).forEach(key => {
      if (!key.includes('transition')) {
        ghostElement.style[key] = computed[key]
      }
    })

    // Set initial position and size with hardware acceleration
    ghost.style.width = `${rect.width}px`
    ghost.style.height = `${rect.height}px`
    ghost.style.transform = `translate3d(${rect.left}px, ${rect.top}px, 0)`
    ghost.style.position = 'fixed'
    ghost.style.top = '0'
    ghost.style.left = '0'
    ghost.style.margin = '0'
    ghost.style.pointerEvents = 'none'
    ghost.style.backfaceVisibility = 'hidden'
    ghost.style.perspective = '1000px'
    ghost.style.transformStyle = 'preserve-3d'
    ghost.style.willChange = 'transform'

    this.log('Ghost created with:', {
      width: ghost.style.width,
      height: ghost.style.height,
      transform: ghost.style.transform,
      classes: ghost.className,
      source
    })

    document.body.appendChild(ghost);
    return ghost;
  }

  moveGhost(ghost, newX, newY) {
    if (!ghost) return;

    requestAnimationFrame(() => {
      ghost.style.transform = `translate3d(${newX}px, ${newY}px, 0)`;
    });
  }

  updatePill(ghost, dropzone, sourceList) {
    if (!ghost) return;

    const pill = ghost.querySelector(".dnd-ghost-pill");
    if (!pill) return;

    let pillText = "No valid drop area"; // Default text
    let isNewList = false;

    if (dropzone) {
      // Check if the dropzone itself is different from the source list
      isNewList = dropzone.element !== sourceList;

      // Use context label if available, otherwise use context name
      if (dropzone.matchedContext) {
        if (dropzone.matchedContext.label) {
          pillText = dropzone.matchedContext.label;
        } else if (dropzone.matchedContext.context) {
          // Fallback to context name if label is missing
          pillText = dropzone.matchedContext.context;
        } else {
          // Fallback if context object is malformed (no label or context name)
          pillText = "Drop Here"; // Generic actionable fallback
        }
      } else {
        // Dropzone exists but no matching context was found
        pillText = "Cannot drop here";
      }
    }

    // Update pill text
    pill.textContent = pillText;

    // Update pill style based on whether it's a new list or sorting within the same list
    if (dropzone && isNewList) {
      pill.classList.remove("sort");
      pill.classList.add("clone");
    } else if (dropzone) {
      // It's the same list (or no list info)
      pill.classList.remove("clone");
      pill.classList.add("sort");
    } else {
      // No dropzone, remove style classes
      pill.classList.remove("sort", "clone");
    }
  }

  animateToPreview(ghost, finalElement, onComplete) {
    const source = ghost.source;
    this.log("animateToPreview", { source, finalElement });

    if (!ghost || !finalElement) {
      this.log("animateToPreview - missing ghost or finalElement", {
        hasGhost: !!ghost,
        hasFinalElement: !!finalElement,
        source,
      });
      if (onComplete) onComplete();
      return;
    }

    const finalRect = finalElement.getBoundingClientRect();

    // Ensure we start a new animation frame to avoid any race conditions
    requestAnimationFrame(() => {
      ghost.classList.add("dropping");
      ghost.style.transform = `translate3d(${finalRect.left}px, ${finalRect.top}px, 0)`;
      ghost.style.opacity = "0";
      ghost.style.width = `${finalRect.width}px`;
      ghost.style.height = `${finalRect.height}px`;

      // Wait for the animation to complete before calling onComplete
      const handleTransitionEnd = (event) => {
        // Only handle the transform transition
        if (event.propertyName === "transform") {
          this.log("animateToPreview - transition complete", {
            source,
            finalElement,
          });
          ghost.removeEventListener("transitionend", handleTransitionEnd);
          if (onComplete) onComplete();
        }
      };

      ghost.addEventListener("transitionend", handleTransitionEnd);
    });
  }

  cleanup() {
    const ghost = document.querySelector(".dnd-ghost");
    if (ghost) {
      ghost.remove();
    }
  }
}

class PreviewManager {
  constructor(ownerController) {
    this.ownerController = ownerController;
    this.debug = ownerController?.debugValue ?? false;
    this.lastPosition = null; // Track last calculated preview position
  }

  log(...args) {
    if (this.ownerController && this.ownerController.debugValue) {
      this.ownerController.debug("PreviewManager:", ...args);
    }
  }

  /**
   * Creates a preview element from a draggable source
   */
  createPreview(draggable) {
    let preview = document.querySelector(".dnd-preview");
    if (!preview) {
      preview = draggable.cloneNode(true);
      preview.classList.add("dnd-preview");
    }
    return preview;
  }

  /**
   * Calculates where to place the preview element based on pointer position and layout
   */
  calculatePreviewPosition(dropzoneElement, pointer, draggable) {
    // Get flex layout information
    const style = getComputedStyle(dropzoneElement);
    const horizontalFlow =
      style.display === "flex" && !style.flexDirection.includes("column");
    const isReverse = style.flexDirection.includes("reverse");
    const isWrap = style.flexWrap !== "nowrap";
    const isFlex = style.display === "flex";
    const columnCount = Number.parseInt(style.columnCount, 10);
    const isMultiColumn = Number.isFinite(columnCount) && columnCount > 1;

    // Get ghost element and determine reference position
    const ghost = draggable.ghost;
    const referencePosition = ghost
      ? {
          x:
            ghost.getBoundingClientRect().left +
            ghost.getBoundingClientRect().width / 2,
          y:
            ghost.getBoundingClientRect().top +
            ghost.getBoundingClientRect().height / 2,
        }
      : {
          x: pointer.x,
          y: pointer.y,
        };

    // Find nearest target based on layout
    const items = Array.from(dropzoneElement.children);
    const itemRects = items.map((item) => ({
      element: item,
      rect: item.getBoundingClientRect(),
    }));

    // Calculate center points for each item
    const itemCenters = itemRects.map(({ rect }) => ({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    }));

    // Get dropzone boundaries
    const dropzoneRect = dropzoneElement.getBoundingClientRect();

    // Find the nearest item based on primary axis
    let nearestItem = null;
    let nearestDistance = Infinity;

    itemRects.forEach((item, i) => {
      const center = itemCenters[i];

      // Calculate distance based on layout. Multi-column lists need both axes,
      // otherwise items in different columns but similar rows look equivalent.
      const primaryDist = horizontalFlow
        ? Math.abs(center.x - referencePosition.x)
        : isMultiColumn
          ? Math.hypot(
              center.x - referencePosition.x,
              center.y - referencePosition.y,
            )
          : Math.abs(center.y - referencePosition.y);

      // Secondary axis only matters for flex layouts
      const secondaryDist = isFlex
        ? horizontalFlow
          ? Math.abs(center.y - referencePosition.y) * 0.5
          : Math.abs(center.x - referencePosition.x) * 0.5
        : 0;

      const distance = primaryDist + secondaryDist;

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestItem = item;
      }
    });

    // If no nearest item found and we have items, append to end
    if (!nearestItem && items.length > 0) {
      const position = { action: "append", element: null };
      return this.compareAndUpdatePosition(position);
    }

    // If no items at all, just append
    if (!nearestItem) {
      const position = { action: "append", element: null };
      return this.compareAndUpdatePosition(position);
    }

    // Determine if we should insert before or after the nearest item
    const nearestCenter = {
      x: nearestItem.rect.left + nearestItem.rect.width / 2,
      y: nearestItem.rect.top + nearestItem.rect.height / 2,
    };

    const insertBefore = horizontalFlow
      ? isReverse
        ? referencePosition.x > nearestCenter.x
        : referencePosition.x < nearestCenter.x
      : isReverse
        ? referencePosition.y > nearestCenter.y
        : referencePosition.y < nearestCenter.y;

    const position = {
      action: "insert",
      element: nearestItem.element,
      before: insertBefore,
    };

    return this.compareAndUpdatePosition(position);
  }

  /**
   * Compare new position with last position and update if different
   */
  compareAndUpdatePosition(newPosition) {
    if (!this.lastPosition) {
      this.lastPosition = newPosition;
      return newPosition;
    }

    // Compare positions
    const hasChanged =
      newPosition.action !== this.lastPosition.action ||
      newPosition.element !== this.lastPosition.element ||
      newPosition.before !== this.lastPosition.before;

    if (hasChanged) {
      this.lastPosition = newPosition;
      return newPosition;
    }

    // Return null if position hasn't changed
    return null;
  }

  /**
   * Cleans up the preview element
   */
  cleanup() {
    const preview = document.querySelector(".dnd-preview");
    if (preview) {
      preview.remove();
      this.lastPosition = null; // Reset last position on cleanup
      this.log("Preview cleaned up by cleanup()");
    }
  }

  /**
   * Removes a specific preview element if provided.
   * @param {HTMLElement} previewElement - The specific preview element to remove.
   */
  removePreviewElement(previewElement) {
    if (previewElement && previewElement.parentNode) {
      previewElement.remove();
      this.lastPosition = null; // Reset last position
      this.log("Preview element explicitly removed", previewElement);
    } else {
      // Fallback to global cleanup if specific element not removable
      this.cleanup();
    }
  }
}

class Dropzone {
  constructor(element, isSelf = false, ownerController) {
    this.element = element;
    this.isSelf = isSelf;
    this.ownerController = ownerController;
    this.debug = ownerController?.debugValue ?? false;
    this.rect = null;
    this.isActive = false;
    this.isAccepting = false;
    this.isRejecting = false;
    this.overlapArea = 0;
    this.previewManager = new PreviewManager(this.ownerController);
    this.currentPreview = null;
    this.matchedContext = null; // Store the context that allowed acceptance
    this.updateRect();
    // Generate unique ID for this dropzone instance
    this.dropzoneId = Math.random().toString(36).substr(2, 9);
  }

  log(...args) {
    if (this.ownerController && this.ownerController.debugValue) {
      const context = {
        dropzoneId: this.dropzoneId,
        isSelf: this.isSelf,
        group: this.element.dataset.dndGroupValue,
        contextsValue: this.element.dataset.dndContextsValue, // Log contexts
        elementContent: this.element.textContent?.trim().slice(0, 50),
        state: {
          isActive: this.isActive,
          isAccepting: this.isAccepting,
          isRejecting: this.isRejecting,
          hasPreview: !!this.currentPreview,
          matchedContext: this.matchedContext, // Log matched context
        },
      };
      const prefix = `Dropzone[${this.dropzoneId}]:`;
      this.ownerController.debug(prefix, ...args, "\nContext:", context);
    }
  }

  updateRect() {
    this.rect = this.element.getBoundingClientRect();
  }

  calculateOverlap(ghostRect) {
    const overlapX =
      Math.min(this.rect.right, ghostRect.right) -
      Math.max(this.rect.left, ghostRect.left);
    const overlapY =
      Math.min(this.rect.bottom, ghostRect.bottom) -
      Math.max(this.rect.top, ghostRect.top);

    if (overlapX > 0 && overlapY > 0) {
      this.overlapArea = overlapX * overlapY;
      return true;
    }

    this.overlapArea = 0;
    return false;
  }

  activate() {
    if (!this.isActive) {
      this.element.classList.add("dnd-potential-dropzone");
      if (this.isSelf) {
        this.element.dataset.dndSelf = "true";
      }
      this.isActive = true;
      this.log("Activated", { isSelf: this.isSelf });
    }
  }

  deactivate() {
    if (this.isActive) {
      // Remove all possible dropzone-related classes
      this.element.classList.remove(
        "dnd-potential-dropzone",
        "dnd-active-dropzone",
        "dnd-rejected-dropzone",
      );
      delete this.element.dataset.dndSelf;
      this.isActive = false;
      this.isAccepting = false;
      this.isRejecting = false;
      this.overlapArea = 0;
      this.removePreview();
      this.log("Deactivated");
    }
  }

  accept() {
    if (!this.isAccepting) {
      this.element.classList.remove("dnd-rejected-dropzone");
      this.element.classList.add("dnd-active-dropzone");
      this.isAccepting = true;
      this.isRejecting = false;
      this.matchedContext = null; // Clear matched context on accept
      this.log("Accepted");
    }
  }

  reject() {
    if (!this.isRejecting) {
      this.element.classList.remove("dnd-active-dropzone");
      this.element.classList.add("dnd-rejected-dropzone");
      this.isAccepting = false;
      this.isRejecting = true;
      this.matchedContext = null; // Clear matched context on reject
      this.removePreview();
      this.log("Rejected");
    }
  }

  getGroup() {
    return this.element.getAttribute("data-dnd-group-value");
  }

  /**
   * Checks if this dropzone can accept the dragged item based on contexts.
   * @param {HTMLElement} sourceControllerElement - The DND controller element where the drag originated.
   * @param {string[]} draggedItemTypes - An array of type strings derived from the dragged item.
   * @returns {object|null} The matching context object if acceptance is allowed, otherwise null.
   */
  canAcceptDrag(sourceControllerElement, draggedItemTypes) {
    const contextsValue = this.element.dataset.dndContextsValue;
    let contexts = [];

    if (!contextsValue) {
      // If no contexts are defined, maybe allow basic group matching as fallback?
      // For now, let's be strict: no contexts = no acceptance (except maybe self? TBD)
      // Alternative: Could check group value match here as a default behavior?
      this.log("canAcceptDrag check failed: No contexts defined");
      return null;
    }

    try {
      contexts = JSON.parse(contextsValue);
      if (!Array.isArray(contexts)) {
        throw new Error("contextsValue is not an array");
      }
    } catch (e) {
      console.error("Failed to parse dndContextsValue JSON:", contextsValue, e);
      this.log("canAcceptDrag check failed: Invalid JSON in contextsValue");
      return null;
    }

    if (!draggedItemTypes || draggedItemTypes.length === 0) {
      this.log(
        "canAcceptDrag check failed: Dragged item has no detected types",
      );
      return null; // Cannot accept an item with no type
    }

    const isSameTarget = sourceControllerElement === this.element;

    for (const ctx of contexts) {
      // Validate context structure (optional but good practice)
      if (!ctx || typeof ctx !== "object" || !Array.isArray(ctx.accepts)) {
        this.log("Skipping invalid context object:", ctx);
        continue;
      }

      // 1. Check sameTargetOnly condition
      const targetMatch =
        (ctx.sameTargetOnly === true && isSameTarget) ||
        (ctx.sameTargetOnly === false && !isSameTarget) ||
        ctx.sameTargetOnly === undefined; // Allow if not specified? Or default to true/false? Let's default to allowing both if undefined.

      if (!targetMatch) {
        //this.log('Context skipped: sameTargetOnly mismatch', { context: ctx.context, isSameTarget, sameTargetOnly: ctx.sameTargetOnly });
        continue;
      }

      // 2. Check if any dragged type is accepted by this context
      const typeMatch = draggedItemTypes.some((draggedType) =>
        ctx.accepts.includes(draggedType),
      );

      if (!typeMatch) {
        //this.log('Context skipped: type mismatch', { context: ctx.context, accepts: ctx.accepts, draggedItemTypes });
        continue;
      }

      // If both conditions pass, this context is a match!
      this.log("canAcceptDrag check passed. Matched context:", ctx);
      return ctx; // Return the first matching context
    }

    // If loop finishes without finding a match
    this.log("canAcceptDrag check failed: No matching context found", {
      draggedItemTypes,
    });
    return null;
  }

  updatePreview(draggable, pointer) {
    if (!this.isAccepting) return;

    // Create preview if it doesn't exist
    if (!this.currentPreview) {
      this.currentPreview = this.previewManager.createPreview(draggable);
    }

    const position = this.previewManager.calculatePreviewPosition(
      this.element,
      pointer,
      draggable,
    );

    // Only update DOM if position changed
    if (position) {
      if (position.action === "append") {
        this.element.appendChild(this.currentPreview);
      } else {
        if (position.before) {
          this.element.insertBefore(this.currentPreview, position.element);
        } else {
          this.element.insertBefore(
            this.currentPreview,
            position.element.nextSibling,
          );
        }

        // Store references
        draggable.preview = this.currentPreview;
        draggable.dropzone = this.element;

        this.log("Preview position updated", {
          action: position.action,
          insertBefore: position.before,
          previewId: this.currentPreview.id,
        });
      }
    }
  }

  removePreview() {
    if (this.currentPreview) {
      this.currentPreview.remove();
      this.currentPreview = null;
      this.log("Preview removed");
    }
  }

  getPreview() {
    return this.currentPreview;
  }
}

class DropzoneManager {
  constructor(ownerController) {
    this.ownerController = ownerController;
    this.debug = ownerController?.debugValue ?? false;
    this.dropzones = new Map(); // element -> Dropzone instance
    this.activeDropzones = new Set(); // Set of active Dropzone instances
    this.currentDropzone = null;
    this.managerId = Math.random().toString(36).substr(2, 9);
  }

  log(...args) {
    if (this.ownerController && this.ownerController.debugValue) {
      const context = {
        managerId: this.managerId,
        activeDropzones: Array.from(this.activeDropzones).map(
          (d) => d.dropzoneId,
        ),
        currentDropzone: this.currentDropzone?.dropzoneId,
      };
      const prefix = `DropzoneManager[${this.managerId}]:`;
      this.ownerController.debug(prefix, ...args, "\nContext:", context);
    }
  }

  /**
   * Find all dropzones that intersect with the ghost element and select the best one
   */
  findIntersectingDropzones(ghost, selfController, sourceGroup) {
    // Get all potential dropzones that are either:
    // 1. The original parent controller
    // 2. Other controllers at the same level with the same parent
    // 3. Controllers with matching groups that aren't inside the dragged element
    const originalParent = selfController.originalParent;
    const draggable = ghost.source;
    const sourceControllerElement =
      DndController.findClosestDndController(draggable); // Get the source controller element

    // Get dragged item types
    let draggedItemTypes = [];
    try {
      if (draggable.dataset.dndDetectedTypes) {
        draggedItemTypes = JSON.parse(draggable.dataset.dndDetectedTypes);
      }
    } catch (e) {
      console.error(
        "Failed to parse dndDetectedTypes JSON:",
        draggable.dataset.dndDetectedTypes,
        e,
      );
    }

    const allDropzoneElements = DndController.queryDndControllers().filter(
      (el) => {
        // Never allow drops into dropzones inside the dragged element
        if (draggable.contains(el)) return false;

        // Get the group of this dropzone
        const dropzoneGroup = el.getAttribute("data-dnd-group-value");

        // First check structural restrictions
        if (originalParent) {
          // Always allow drops in the original parent
          if (el === originalParent) return true;

          // Allow drops in siblings (same parent, same level)
          const elParent = DndController.findClosestDndController(
            el.parentElement,
          );
          const sameLevel =
            elParent ===
            DndController.findClosestDndController(
              originalParent.parentElement,
            );
          if (sameLevel) return true;

          // For other dropzones, allow if they have a matching group
          if (dropzoneGroup && dropzoneGroup === sourceGroup) {
            // Check if this dropzone is a valid target based on hierarchy
            const isNestedInDraggable =
              DndController.findClosestDndController(el)?.contains(draggable);
            const isDraggableInDropzone =
              DndController.findClosestDndController(draggable)?.contains(el);

            // Allow if:
            // 1. The dropzone is not nested inside the draggable
            // 2. The draggable is not nested inside the dropzone
            // 3. They share the same group
            return (
              !isNestedInDraggable &&
              !isDraggableInDropzone &&
              dropzoneGroup === sourceGroup
            );
          }
        }

        return false;
      },
    );

    const ghostRect = ghost.getBoundingClientRect();

    // Track which dropzones are still intersecting
    const currentlyIntersecting = new Set();

    let bestDropzone = null;
    let maxOverlap = 0;

    // Process each dropzone
    allDropzoneElements.forEach((element) => {
      // Get or create Dropzone instance
      let dropzone = this.dropzones.get(element);
      if (!dropzone) {
        const isSelf = element === selfController.element;
        dropzone = new Dropzone(element, isSelf, this.ownerController);
        this.dropzones.set(element, dropzone);
      }

      // Update rect and check intersection
      dropzone.updateRect();
      if (dropzone.calculateOverlap(ghostRect)) {
        currentlyIntersecting.add(dropzone);

        // Only activate if not already active
        if (!this.activeDropzones.has(dropzone)) {
          dropzone.activate();
          this.activeDropzones.add(dropzone);
        }

        // Check if this dropzone can accept the drag using the new context logic
        const matchedContext = dropzone.canAcceptDrag(
          sourceControllerElement,
          draggedItemTypes,
        );

        if (matchedContext && dropzone.overlapArea > maxOverlap) {
          // Store the matched context before comparing overlap
          dropzone.matchedContext = matchedContext;
          maxOverlap = dropzone.overlapArea;
          bestDropzone = dropzone;
        } else if (!matchedContext) {
          // If it can't accept based on context, ensure its context is cleared
          dropzone.matchedContext = null;
        }
      }
    });

    // Deactivate dropzones that are no longer intersecting
    for (const dropzone of this.activeDropzones) {
      if (!currentlyIntersecting.has(dropzone)) {
        dropzone.deactivate();
        this.activeDropzones.delete(dropzone);
      }
    }

    // Update dropzone states and handle transitions
    this.activeDropzones.forEach((dropzone) => {
      // Acceptance/rejection is now based on whether it's the bestDropzone
      // and whether it had a matchedContext to begin with
      if (dropzone === bestDropzone) {
        if (!dropzone.isAccepting) {
          // If this is a new best dropzone, clean up the old one first
          if (this.currentDropzone && this.currentDropzone !== dropzone) {
            // Reject the old one only if it wasn't accepting
            if (!this.currentDropzone.isAccepting) {
              this.currentDropzone.reject();
            }
            this.currentDropzone.removePreview();
          }
          dropzone.accept(); // Accept the new best dropzone
        }
      } else if (dropzone.matchedContext) {
        // If it could potentially accept (had a matched context) but wasn't the best
        if (!dropzone.isRejecting) {
          dropzone.reject(); // Show as rejected but potentially valid
          dropzone.removePreview(); // Remove any existing preview
        }
      } else {
        // If it never had a matched context, ensure it's deactivated
        if (dropzone.isActive) {
          dropzone.deactivate();
        }
      }
    });

    // Update current dropzone reference
    if (this.currentDropzone !== bestDropzone) {
      this.currentDropzone = bestDropzone;
      this.log("Current dropzone changed:", {
        from: this.currentDropzone?.dropzoneId || "none",
        to: bestDropzone?.dropzoneId || "none",
        overlap: maxOverlap,
      });
    }

    return this.currentDropzone;
  }

  /**
   * Get the current best matching dropzone
   */
  getCurrentDropzone() {
    return this.currentDropzone;
  }

  /**
   * Clean up all dropzones
   */
  cleanup() {
    // Clean up all dropzones
    this.activeDropzones.forEach((dropzone) => dropzone.deactivate());
    this.activeDropzones.clear();
    this.currentDropzone = null;

    // Clean up any remaining visual helpers that might have been missed
    document
      .querySelectorAll(
        ".dnd-potential-dropzone, .dnd-active-dropzone, .dnd-rejected-dropzone",
      )
      .forEach((el) => {
        el.classList.remove(
          "dnd-potential-dropzone",
          "dnd-active-dropzone",
          "dnd-rejected-dropzone",
        );
      });
  }
}

class OriginalManager {
  constructor(element, ownerController) {
    this.element = element;
    this.ownerController = ownerController;
    this.debug = ownerController?.debugValue ?? false;
    this.originalParent = null;
    this.originalIndex = -1;
    this.cleanClone = null; // To store the clone created at drag start
    this.cleanCloneOriginalId = null;
    this.removeOriginalMode = "ondrop"; // Stores the FINAL mode for finalizeAfterDrop
    this.isPreviewingRemoval = false;
    this.currentHoverContext = null;
    this.log("OriginalManager initialized", { element });
  }

  log(...args) {
    if (this.ownerController && this.ownerController.debugValue) {
      this.ownerController.debug("OriginalManager:", ...args);
    }
  }

  storeOriginalPosition() {
    if (!this.element) return;
    this.originalParent = this.element.parentElement;
    if (this.originalParent) {
      this.originalIndex = Array.from(this.originalParent.children).indexOf(
        this.element,
      );
    }
    // Make sure cleanClone is created before storing position if it affects calculations
    if (!this.cleanClone) {
      this.createCleanClone(); // Ensure clone exists for accurate dimension/state capture if needed
    }
    this.log("Stored original position", {
      parent: this.originalParent,
      index: this.originalIndex,
    });
  }

  markAsSource() {
    if (!this.element) return;
    this.element.classList.add("dnd-source");
    this.log("Marked as source");
  }

  createCleanClone() {
    if (!this.element) return;
    this.prepareOwnedControllersForClone();
    this.cleanCloneOriginalId = this.element.id || null;
    this.cleanClone = this.element.cloneNode(true);
    // Remove ID to prevent duplicates, maybe other attributes if needed?
    this.cleanClone.removeAttribute("id");
    // Clear potentially problematic state classes/attributes from clone?
    this.cleanClone.classList.remove("dnd-source", "removing");
    // Remove any dnd-controller attributes from the clone to prevent nested controller issues
    Array.from(this.cleanClone.attributes).forEach((attr) => {
      if (attr.name.startsWith("data-dnd-")) {
        this.cleanClone.removeAttribute(attr.name);
      }
    });
    if (
      this.cleanClone.dataset.controller &&
      this.cleanClone.dataset.controller.includes("dnd")
    ) {
      // More robustly remove dnd if it's part of multiple controllers
      const controllers = this.cleanClone.dataset.controller.split(" ");
      const filteredControllers = controllers.filter((c) => c !== "dnd");
      if (filteredControllers.length > 0) {
        this.cleanClone.dataset.controller = filteredControllers.join(" ");
      } else {
        delete this.cleanClone.dataset.controller;
      }
    }
    this.cleanClone.style.cssText = ""; // Clear inline styles
    this.log("Created clean clone", this.cleanClone);
  }

  prepareOwnedControllersForClone() {
    const controllerElements = new Set();

    if (this.element.matches?.("[data-controller]")) {
      controllerElements.add(this.element);
    }

    this.element.querySelectorAll?.("[data-controller]").forEach((element) => {
      controllerElements.add(element);
    });

    controllerElements.forEach((element) => {
      element.dispatchEvent(
        new CustomEvent("dnd:before-clone", {
          bubbles: false,
          detail: { source: this.element },
        }),
      );
    });

    this.element.dispatchEvent(
      new CustomEvent("dnd:before-clone", {
        bubbles: true,
        detail: { source: this.element },
      }),
    );
  }

  updateHoverState(hoverContext, isSameTargetReorderContext = false) {
    this.currentHoverContext = hoverContext;
    const hoverRemovePolicy = hoverContext?.removeOriginal;

    if (hoverRemovePolicy === "onbeforedrop") {
      if (!this.isPreviewingRemoval) {
        this.startCollapseAnimation(null, {
          animate: !isSameTargetReorderContext,
        });
        this.isPreviewingRemoval = true;
        this.log('Hover: Started previewing removal for "onbeforedrop"', {
          isSameTargetReorderContext,
        });
      }
    } else {
      // hover policy is 'ondrop', 'keep', null, or undefined
      if (this.isPreviewingRemoval) {
        this.restore(); // This should also stop animation
        this.isPreviewingRemoval = false;
        this.log("Hover: Stopped previewing removal, restoring original");
      }
    }
  }

  prepareForDrop(finalDropContext, isSameTargetReorderContext = false) {
    let finalActualRemoveMode = "ondrop"; // Default assumption

    if (finalDropContext?.removeOriginal) {
      finalActualRemoveMode = finalDropContext.removeOriginal;
    } else if (finalDropContext?.isMove === false) {
      // Fallback for old isMove syntax if removeOriginal not present
      finalActualRemoveMode = "keep";
    } else if (finalDropContext?.isMove === true) {
      // Fallback for old isMove syntax
      finalActualRemoveMode = "ondrop";
    }
    // if no removeOriginal and no isMove, defaults to 'ondrop'

    this.removeOriginalMode = finalActualRemoveMode; // Set the mode for finalizeAfterDrop

    this.log("Preparing for final drop", {
      finalMode: this.removeOriginalMode,
      wasPreviewingRemoval: this.isPreviewingRemoval,
      currentHoverContext: this.currentHoverContext,
    });

    if (this.removeOriginalMode === "ondrop") {
      if (!this.isPreviewingRemoval) {
        // Only start collapse if not already collapsed by 'onbeforedrop'
        this.log('Starting collapse for "ondrop" mode (was not previewing).');
        this.startCollapseAnimation(null, {
          animate: !isSameTargetReorderContext,
        });
      } else {
        this.log(
          '"ondrop" mode, but was already previewing removal (onbeforedrop). No new animation.',
        );
      }
    } else if (this.removeOriginalMode === "keep") {
      if (this.isPreviewingRemoval) {
        // Was 'onbeforedrop', now must be 'keep'
        this.log('Restoring for "keep" mode (was previewing removal).');
        this.restore(); // Make sure it's visible
      }
      this.isPreviewingRemoval = false; // Not previewing removal anymore if mode is 'keep'
    } else if (this.removeOriginalMode === "onbeforedrop") {
      // It should already be collapsed due to updateHoverState.
      if (!this.isPreviewingRemoval) {
        this.log(
          'WARN: "onbeforedrop" final mode, but was not previewing. Forcing collapse.',
        );
        this.startCollapseAnimation(null, {
          animate: !isSameTargetReorderContext,
        });
        this.isPreviewingRemoval = true; // Ensure state is correct
      } else {
        this.log(
          '"onbeforedrop" final mode, was correctly previewing removal.',
        );
      }
    }
  }

  finalizeAfterDrop(success) {
    this.log("Finalizing after drop", {
      success,
      removeOriginalMode: this.removeOriginalMode,
    });
    if (success) {
      if (this.removeOriginalMode !== "keep") {
        this.log("Remove original element from DOM");
        this.removeFromDOM();
      } else {
        this.log("Restoring original, as it is kept");
        this.restoreStylesOnly();
      }
    } else {
      this.log("Finalizing Original: Restoring (drop failed)");
      this.restore();
    }
    // Reset states after finalization
    this.isPreviewingRemoval = false;
    this.currentHoverContext = null;
  }

  stopCollapseAnimation() {
    cancelAnimationFrame(this.collapseAnimationFrameId);
    this.collapseAnimationFrameId = null;
  }

  startCollapseAnimation(onComplete, options = { animate: true }) {
    this.stopCollapseAnimation();
    this.log("Starting collapse/hide logic", { options });
    if (!this.element || !this.originalParent) {
      this.log("Cannot start collapse/hide - element or parent missing");
      if (onComplete) onComplete();
      return;
    }

    const draggable = this.element;
    const originalParent = this.originalParent;

    draggable.classList.add("dnd-source");
    draggable.classList.add("removing"); // Keep for consistent styling/state

    if (!options.animate) {
      this.log("Applying instant hide (display: none) synchronously.");
      draggable.style.display = "none";
      // No need to also set opacity to 0 or collapse dimensions if display:none is used for instant hide.
      // However, ensure overflow is hidden if there was any content that display:none might not clip immediately in some edge cases (though unlikely).
      draggable.style.overflow = "hidden";

      if (onComplete) onComplete();
      return;
    }

    // --- Full Animation Logic (options.animate is true) ---
    this.log("Starting full collapse animation.");
    // Move original to bottom of its parent to maintain layout during collapse animation
    if (
      draggable.parentElement === originalParent &&
      originalParent.lastElementChild !== draggable
    ) {
      originalParent.appendChild(draggable);
      this.log("Moved original to end of parent for animation.");
    }

    // Get initial dimensions and computed styles
    const rect = draggable.getBoundingClientRect();
    const style = window.getComputedStyle(draggable);
    const horizontalFlow =
      style.display === "flex" && !style.flexDirection.includes("column");
    const startHeight = rect.height;
    const startWidth = rect.width;
    const startOpacity = parseFloat(style.opacity) || 0.5; // Use computed opacity or default

    // Get compensation margins if it was the first element *before* moving
    const isFirst = this.originalIndex === 0;
    let compensateMarginTop = 0;
    let compensateMarginLeft = 0;

    if (isFirst && originalParent) {
      // Find the element that *was* originally after it
      const nextElement = originalParent.children[this.originalIndex + 1];
      if (nextElement) {
        const nextStyle = window.getComputedStyle(nextElement);
        compensateMarginTop = -parseFloat(nextStyle.marginTop);
        compensateMarginLeft = -parseFloat(nextStyle.marginLeft);
        this.log("Calculated compensation margins", {
          top: compensateMarginTop,
          left: compensateMarginLeft,
        });
      }
    }

    // Set initial state for animation
    draggable.style.overflow = "hidden"; // Crucial for collapse effect
    draggable.style.transition = "none"; // Disable CSS transitions during manual animation
    // Use clipPath for potentially smoother animation if supported, though height/width is often fine
    // draggable.style.clipPath = 'inset(0 0 0 0)';

    // Animation duration and timing
    const duration = 300; // Match ghost animation duration if possible
    const startTime = performance.now();
    const easing = (t) => t * t; // Quad ease-in - faster at end

    // If not animating, apply final state and complete
    if (!options.animate) {
      this.log("Skipping collapse animation, applying instant hide.");
      if (horizontalFlow) {
        draggable.style.width = `0px`;
        draggable.style.maxWidth = `0px`;
        draggable.style.minWidth = `0px`;
        draggable.style.height = `${startHeight}px`;
      } else {
        draggable.style.height = `0px`;
        draggable.style.maxHeight = `0px`;
        draggable.style.minHeight = `0px`;
        draggable.style.width = `${startWidth}px`;
      }
      draggable.style.opacity = "0";
      draggable.style.paddingTop = "0px";
      draggable.style.paddingBottom = "0px";
      draggable.style.paddingLeft = "0px";
      draggable.style.paddingRight = "0px";
      draggable.style.marginTop = "0px";
      draggable.style.marginBottom = "0px";
      draggable.style.marginLeft = "0px";
      draggable.style.marginRight = "0px";
      draggable.style.borderTopWidth = "0px";
      draggable.style.borderBottomWidth = "0px";
      draggable.style.borderLeftWidth = "0px";
      draggable.style.borderRightWidth = "0px";
      draggable.style.overflow = "hidden"; // Still hide overflow

      if (onComplete) onComplete();
      return;
    }

    // We no longer need the removeElements function as finalize handles it

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easing(progress);

      // Calculate current values FIRST
      const currentOpacity = startOpacity * (1 - easedProgress);

      // Add logging inside the animation loop
      if (progress < 1 && progress > 0) {
        if (!this.lastLogTime || currentTime - this.lastLogTime > 100) {
          // Log less frequently
          // Log calculated style values
          let logStyles = {
            progress: progress.toFixed(2),
            opacity: currentOpacity.toFixed(3),
          };
          if (horizontalFlow) {
            logStyles.width = `${(startWidth * (1 - easedProgress)).toFixed(3)}px`;
          } else {
            logStyles.height = `${(startHeight * (1 - easedProgress)).toFixed(3)}px`;
          }
          this.log("Collapse animation frame:", logStyles);
          this.lastLogTime = currentTime;
        }
      }

      // Animate the primary axis (height for vertical, width for horizontal)
      if (horizontalFlow) {
        const currentWidth = startWidth * (1 - easedProgress);
        draggable.style.width = `${currentWidth.toFixed(3)}px`;
        draggable.style.maxWidth = `${currentWidth.toFixed(3)}px`;
        draggable.style.minWidth = `0px`; // Prevent text wrap issues
        draggable.style.height = `${startHeight}px`; // Keep height constant
      } else {
        const currentHeight = startHeight * (1 - easedProgress);
        draggable.style.height = `${currentHeight.toFixed(3)}px`;
        draggable.style.maxHeight = `${currentHeight.toFixed(3)}px`;
        draggable.style.minHeight = `0px`;
        draggable.style.width = `${startWidth}px`; // Keep width constant
      }

      // For first elements, animate to compensation margin
      if (isFirst) {
        const currentMarginTop = compensateMarginTop * easedProgress;
        const currentMarginLeft = compensateMarginLeft * easedProgress;
        // Apply compensation margin adjustments (combine with general margin animation below)
      }

      draggable.style.opacity = currentOpacity.toFixed(3);
      // Also animate padding/margins/border to zero for full collapse
      draggable.style.paddingTop = `${parseFloat(style.paddingTop) * (1 - easedProgress)}px`;
      draggable.style.paddingBottom = `${parseFloat(style.paddingBottom) * (1 - easedProgress)}px`;
      draggable.style.paddingLeft = `${parseFloat(style.paddingLeft) * (1 - easedProgress)}px`;
      draggable.style.paddingRight = `${parseFloat(style.paddingRight) * (1 - easedProgress)}px`;
      // Combine base margin animation with potential compensation margin
      draggable.style.marginTop = `${parseFloat(style.marginTop) * (1 - easedProgress) + (isFirst ? compensateMarginTop * easedProgress : 0)}px`;
      draggable.style.marginBottom = `${parseFloat(style.marginBottom) * (1 - easedProgress)}px`;
      draggable.style.marginLeft = `${parseFloat(style.marginLeft) * (1 - easedProgress) + (isFirst ? compensateMarginLeft * easedProgress : 0)}px`;
      draggable.style.marginRight = `${parseFloat(style.marginRight) * (1 - easedProgress)}px`;
      draggable.style.borderTopWidth = `${parseFloat(style.borderTopWidth) * (1 - easedProgress)}px`;
      draggable.style.borderBottomWidth = `${parseFloat(style.borderBottomWidth) * (1 - easedProgress)}px`;
      draggable.style.borderLeftWidth = `${parseFloat(style.borderLeftWidth) * (1 - easedProgress)}px`;
      draggable.style.borderRightWidth = `${parseFloat(style.borderRightWidth) * (1 - easedProgress)}px`;

      // Continue animation if not complete
      if (progress < 1) {
        this.collapseAnimationFrameId = requestAnimationFrame(animate);
      } else {
        this.collapseAnimationFrameId = null;
        this.log("Collapse animation complete");
        if (onComplete) onComplete();
      }
    };

    this.collapseAnimationFrameId = requestAnimationFrame(animate);
  }

  restore() {
    this.log("Restoring original element");
    if (this.element) {
      // Modal GET navigation can restore before the collapse finishes.
      // Stop it before clearing styles so its next frame cannot hide us again.
      this.stopCollapseAnimation();
      this.element.classList.remove("dnd-source", "removing");
      this.element.style.cssText = ""; // Clear ALL inline styles
      this.element.style.opacity = "1"; // Ensure visible

      // Attempt to move back to original position
      if (this.originalParent && !this.originalParent.isConnected) {
        // A canvas refresh replaced the list we dragged out of. Moving the
        // element back into that detached parent would take it off the page.
        this.log("Not moving element back - original parent is detached");
      } else if (this.originalParent && this.originalIndex !== -1) {
        if (this.originalIndex < this.originalParent.children.length) {
          this.originalParent.insertBefore(
            this.element,
            this.originalParent.children[this.originalIndex],
          );
          this.log("Moved element back to original index", this.originalIndex);
        } else {
          this.originalParent.appendChild(this.element); // Fallback: append if index out of bounds
          this.log("Appended element back to original parent (index invalid)");
        }
      } else {
        this.log(
          "Cannot move element back to original position - parent/index unknown",
        );
      }
    }
    // Ensure previewing state is reset if restore is called directly
    // this.isPreviewingRemoval = false; // This might be too aggressive here, let finalizeAfterDrop or cleanup handle it.
  }

  // A canvas refresh landed between the drop and the finalize, so the server
  // render -- not this drag -- owns the list now. Drop the drag's own state
  // without moving or removing a single node: the element we were holding may
  // be the very node the refresh matched by id and put back on the page.
  releaseAfterRefresh() {
    this.log("Releasing after a refresh re-rendered the drop target");
    if (this.element?.isConnected) {
      // The refresh kept this node. Clear what the drag styled on it in case
      // the morph preserved the inline collapse.
      this.restoreStylesOnly();
    }
    this.isPreviewingRemoval = false;
    this.currentHoverContext = null;
  }

  removeFromDOM() {
    this.log(
      "Removing original element from DOM (called by finalizeAfterDrop or explicit cleanup)",
    );
    if (this.element && this.element.parentNode) {
      this.element.remove();
    }
  }

  restoreStylesOnly() {
    this.stopCollapseAnimation();
    this.log(
      "Restoring styles for non-moved original element (called by finalizeAfterDrop)",
    );
    if (this.element) {
      this.element.classList.remove("dnd-source", "removing");
      this.element.style.cssText = "";
      this.element.style.opacity = "1";
      // Should NOT reposition it
    }
  }

  cleanup() {
    this.log("Cleanup original manager");
    this.stopCollapseAnimation();
    if (this.element) {
      // If the element is still in the DOM and not part of a successful 'move'
      // (which would have removed it), restore its state.
      if (this.isPreviewingRemoval && this.element.parentNode) {
        // Explicitly restore if it was previewing removal
        this.log("Cleaning up: Restoring element that was previewing removal.");
        this.restore();
      } else if (this.element.parentNode) {
        // General restore if still in DOM (covers failed drags)
        this.log("Cleaning up: Element still in DOM, ensuring restored state.");
        // The existing restore logic within cleanup might be sufficient if this.restore() is robust.
        // The original logic for restore within cleanup:
        this.element.classList.remove("dnd-source", "removing");
        this.element.style.cssText = "";
        this.element.style.opacity = "1";
        if (this.originalParent && !this.originalParent.isConnected) {
          this.log("Cleaning up: original parent is detached, leaving element where it is");
        } else if (this.originalParent && this.originalIndex !== -1) {
          if (this.originalIndex < this.originalParent.children.length) {
            this.originalParent.insertBefore(
              this.element,
              this.originalParent.children[this.originalIndex],
            );
          } else {
            this.originalParent.appendChild(this.element);
          }
        }
        this.log("Restored element state during cleanup");
      }
      delete this.element.ghost;
      delete this.element.dropzone;
    }
    this.cleanClone = null; // Clear clone on cleanup
    this.cleanCloneOriginalId = null;
    this.isPreviewingRemoval = false; // Reset state
    this.currentHoverContext = null; // Reset state
  }
}

class DropStatusManager {
  constructor(ownerController) {
    this.ownerController = ownerController;
    this.debug = ownerController?.debugValue ?? false;
    this.pillElement = null;
    this.fadeTimeoutId = null;
    this.log("DropStatusManager initialized");
  }

  log(...args) {
    if (this.ownerController && this.ownerController.debugValue) {
      this.ownerController.debug("DropStatusManager:", ...args);
    }
  }

  /** Attaches the primary status pill (usually to the preview) */
  attachTo(targetElement) {
    this.log("Attaching pill to", targetElement);
    this.cleanup(); // Ensure any previous pill is gone
    if (!targetElement) return null;

    this.pillElement = document.createElement("div");
    this.pillElement.classList.add("dnd-drop-status-pill");

    if (getComputedStyle(targetElement).position === "static") {
      targetElement.style.position = "relative";
    }
    targetElement.appendChild(this.pillElement);
    this.updateStatus("performing", "Performing..."); // Set initial state
    return this.pillElement;
  }

  /** Updates the status of the primary pill (on the preview) */
  updateStatus(status, message) {
    if (!this.pillElement) {
      this.log("Update status called but no pill exists");
      return;
    }
    this.log("Updating status:", { status, message });

    this.pillElement.textContent = message;
    this.pillElement.classList.remove("success", "error");
    this.pillElement.style.opacity = "1";

    let duration =
      status === "success" ? 500 : status === "error" ? 2000 : 30000; // Keep performing long?

    if (this.fadeTimeoutId) {
      clearTimeout(this.fadeTimeoutId);
    }

    if (status === "success") this.pillElement.classList.add("success");
    if (status === "error") this.pillElement.classList.add("error");

    // Set timer to fade and then remove THIS pill
    this.fadeTimeoutId = setTimeout(() => {
      // Check reference still exists before removing
      const pillToRemove = this.pillElement;
      if (pillToRemove) {
        this.pillElement.style.opacity = "0";
        // Cleanup removes this.pillElement reference
        setTimeout(() => {
          this.cleanup();
        }, 200); // Call cleanup for the main pill
      }
      this.fadeTimeoutId = null;
    }, duration);
  }

  /** Creates and shows a status pill on the final dropped element */
  showStatusOnFinalElement(finalElement, status, message) {
    if (!finalElement) return;
    this.log("Showing final status on:", { finalElement, status, message });

    // Create a NEW pill element
    const finalPill = document.createElement("div");
    finalPill.classList.add("dnd-drop-status-pill");
    finalPill.textContent = message;
    finalPill.style.opacity = "1"; // Start visible

    if (status === "success") finalPill.classList.add("success");
    if (status === "error") finalPill.classList.add("error");

    // Ensure target can position it
    if (getComputedStyle(finalElement).position === "static") {
      finalElement.style.position = "relative";
    }
    finalElement.appendChild(finalPill);

    // Set its own fade-out timer
    const duration = status === "success" ? 1500 : 3000; // Let final pill stay a bit longer
    const timeoutId = setTimeout(() => {
      // Store timeout ID on the PARENT element
      finalElement.dataset.dndStatusTimeoutId = timeoutId.toString();

      // Clear the timeout ID from the parent element's dataset when timeout executes
      if (
        finalElement &&
        finalElement.dataset.dndStatusTimeoutId === timeoutId.toString()
      ) {
        delete finalElement.dataset.dndStatusTimeoutId;
      }

      const pillToRemove = finalPill; // Capture reference for timeout
      const parentElement = finalElement; // Capture parent reference
      this.log(`Setting timeout ${timeoutId} for final status pill`);

      pillToRemove.style.opacity = "0";
      setTimeout(() => {
        if (pillToRemove && pillToRemove.parentNode) {
          pillToRemove.remove();
          this.log(
            `Removed final status pill (timeout ${timeoutId} completed)`,
          );
        } else {
          this.log(
            `Final status pill (timeout ${timeoutId}) already removed or detached.`,
          );
        }
      }, 200); // Remove after fade
    }, duration);
  }

  cleanup(specificPill = null) {
    // Cleans up the primary pill (this.pillElement) and its timeout
    if (this.fadeTimeoutId) {
      this.log("Clearing fade timeout for primary pill");
      clearTimeout(this.fadeTimeoutId);
      this.fadeTimeoutId = null;
    }
    if (this.pillElement) {
      this.log("Cleaning up primary status pill");
      this.pillElement.remove();
      this.pillElement = null; // Clear the reference
    }
  }
}

class AutoScrollManager {
  constructor(controller) {
    this.controller = controller;
    this.log("AutoScrollManager CREATED", {
      controllerId: controller.controllerId
    });
    this.scrollableContainers = new Set();
    this.activeScrolls = new Map();
    this.scrollThreshold = 50; // px inside an edge where the pointer starts to scroll it
    this.outsideMargin = 48; // px beyond an edge the pointer may push and still scroll it
    this.scrollSpeed = 8; // px per scroll step at intensity 1
    this.maxScrollSpeed = 64; // px per scroll step, so at most ~1,300px/s at full push
    this.scrollInterval = 50 //16; // ms between scroll steps (~60fps)

    // Caching for performance
    this.containerCache = new Map(); // Cache scrollable container info
    this.lastCacheTime = 0;
    this.cacheTimeout = 1000; // Cache valid for 1 second
    this.dragStartTime = 0; // Track when current drag started

    // Scroll snap management
    this.originalScrollSnap = new Map(); // Store original scroll-snap values
    this.scrollSnapProperties = ['scrollSnapType', 'scrollSnapAlign', 'scrollSnapStop'];
  }

  log(message, data = {}) {
    if (this.controller.debugValue) {
      console.log(`[AutoScrollManager] ${message}`, {
        controllerId: this.controller.controllerId,
        ...data,
      });
    }
  }

  /**
   * Called during drag move to check if auto-scrolling should occur
   * @param {HTMLElement} ghost - The ghost element being dragged
   * @param {Event} event - The drag move event
   */
  onDragMove(ghost, event) {
    // Safety check - don't do anything if drag isn't active
    if (!this.controller.isDragging) {
      this.log("onDragMove called but drag not active, cleaning up");
      this.cleanup();
      return;
    }

    // Start continuous scrolling on first drag move
    if (!this.continuousScrollInterval) {
      this.disableScrollSnap();
      this.continuousScrollInterval = setInterval(() => {
        this.performContinuousScroll();
      }, this.scrollInterval);
      this.log("Started continuous scroll interval");
    }

    this.log("onDragMove called", {
      ghostPosition: ghost ? { x: ghost.offsetLeft, y: ghost.offsetTop } : null,
      pointerPosition: { x: event.clientX, y: event.clientY }
    });

    // Which containers may scroll is decided by where the POINTER is, never
    // by what the ghost overlaps: a drawer row's ghost is as wide as the
    // sidebar panel, so it kept overlapping the panel's list while the
    // pointer was already over the board, and the list raced away (#269).
    const pointer = { x: event.clientX, y: event.clientY };
    const candidates = this.findScrollableContainers(ghost, pointer);
    const pointerContainers = candidates
      .filter(container => container.pointerInside && !container.isPage)
      .map(container => container.element);

    // A container arms once the pointer has been in its interior -- inside,
    // and clear of the edge zones. Crossing INTO a container passes through
    // an edge zone first, and that must not scroll it: entering the board
    // from the docked sidebar crosses the board's right edge. The containers
    // the drag starts in arm at once, so a row grabbed at the bottom of a
    // list can still push the list down.
    const firstMove = !this.armedContainers;
    if (firstMove) this.armedContainers = new Set();

    this.lastScrollContainers = candidates.map(container => {
      if (container.pointerInside && (firstMove || this.isInterior(container, pointer))) {
        this.armedContainers.add(container.element);
      }
      if (!this.armedContainers.has(container.element)) return null;

      const directions = this.calculateScrollDirection(container, pointer, pointerContainers);
      if (!directions) return null;

      // Disable scroll snap for this container if needed
      this.disableScrollSnapForContainer(container.element, container.isPage);

      this.log("Updated scroll target", {
        element: container.element.tagName + (container.element.id ? `#${container.element.id}` : ''),
        directions: Object.keys(directions)
      });

      return { container, directions };
    }).filter(Boolean);

    this.log("Updated scroll targets", {
      count: this.lastScrollContainers.length
    });
  }

  /**
   * Perform continuous scrolling for stored containers (when mouse is stationary)
   */
  performContinuousScroll() {
    // Safety check - stop if drag is no longer active
    if (!this.controller.isDragging) {
      this.log("Continuous scroll stopped - drag not active");
      if (this.continuousScrollInterval) {
        clearInterval(this.continuousScrollInterval);
        this.continuousScrollInterval = null;
      }
      return;
    }

    // Safety check - stop if no containers to scroll
    if (this.lastScrollContainers.length === 0) {
      return;
    }

    for (const { container, directions } of this.lastScrollContainers) {
      if (directions && container) {
        this.performScroll(container, directions);
      }
    }
  }

  /**
   * The scrollable containers the pointer is inside of, or within
   * `outsideMargin` beyond -- the ones a drag could be asking to scroll.
   * @param {HTMLElement} ghost - The ghost element (skipped, with whatever contains it)
   * @param {{x: number, y: number}} pointer - The pointer position
   * @returns {Array} Array of container info objects
   */
  findScrollableContainers(ghost, pointer) {
    this.log("findScrollableContainers called");

    if (!ghost) return [];

    const now = Date.now();
    const margin = this.outsideMargin;
    const containers = [];
    const near = rect =>
      pointer.x >= rect.left - margin && pointer.x <= rect.right + margin &&
      pointer.y >= rect.top - margin && pointer.y <= rect.bottom + margin;
    const inside = rect =>
      pointer.x >= rect.left && pointer.x <= rect.right &&
      pointer.y >= rect.top && pointer.y <= rect.bottom;

    // Check if we can use cached container list
    // Don't refresh cache during active drag unless it's really old
    const isDragActive = this.controller.isDragging;
    const timeSinceLastCache = now - this.lastCacheTime;
    const shouldRefreshCache = !this.containerCache.size ||
                              (!isDragActive && timeSinceLastCache > this.cacheTimeout) ||
                              (isDragActive && timeSinceLastCache > this.cacheTimeout * 5); // Longer timeout during drag

    // Page-level scrolling: the viewport is the container, and the pointer
    // is always inside it.
    const documentElement = document.documentElement;
    const body = document.body;
    const pageCanScrollX = documentElement.scrollWidth > documentElement.clientWidth ||
                          body.scrollWidth > body.clientWidth;
    const pageCanScrollY = documentElement.scrollHeight > documentElement.clientHeight ||
                          body.scrollHeight > body.clientHeight;

    if (pageCanScrollX || pageCanScrollY) {
      containers.push({
        element: documentElement,
        rect: { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight },
        canScrollX: pageCanScrollX,
        canScrollY: pageCanScrollY,
        isPage: true,
        pointerInside: true
      });
    }

    // Build or refresh container cache if needed
    if (shouldRefreshCache) {
      this.refreshContainerCache();
      this.lastCacheTime = now;

      // Track drag start time for first cache during drag
      if (isDragActive && this.dragStartTime === 0) {
        this.dragStartTime = now;
      }
    }

    for (const [element, containerInfo] of this.containerCache) {
      // Skip if it's the ghost itself or contains the ghost
      if (element === ghost || element.contains(ghost)) continue;

      // Skip if it's inside the dragged element
      const draggable = ghost.source;
      if (draggable && draggable.contains(element)) continue;

      // Positions can change during drag, so measure now
      const rect = element.getBoundingClientRect();
      if (!near(rect)) continue;

      containers.push({
        element: element,
        rect: rect,
        canScrollX: containerInfo.canScrollX,
        canScrollY: containerInfo.canScrollY,
        isPage: false,
        pointerInside: inside(rect)
      });
    }

    this.log("Found scrollable containers at pointer", {
      count: containers.length,
      containers: containers.map(c => ({
        element: c.element.tagName + (c.element.id ? `#${c.element.id}` : ''),
        canScrollX: c.canScrollX,
        canScrollY: c.canScrollY,
        pointerInside: c.pointerInside
      }))
    });

    return containers;
  }

  /**
   * Whether the pointer is inside the container and clear of its edge zones
   * on every axis the container can scroll.
   * @param {Object} containerInfo - Container info object
   * @param {{x: number, y: number}} pointer - The pointer position
   * @returns {boolean}
   */
  isInterior(containerInfo, pointer) {
    const { rect, canScrollX, canScrollY } = containerInfo;
    const threshold = this.scrollThreshold;
    const clearX = !canScrollX ||
      (pointer.x - rect.left > threshold && rect.right - pointer.x > threshold);
    const clearY = !canScrollY ||
      (pointer.y - rect.top > threshold && rect.bottom - pointer.y > threshold);
    return clearX && clearY;
  }

  /**
   * Which edges of a container the pointer is asking to scroll past. An edge
   * scrolls while the pointer is within `scrollThreshold` inside it, and
   * keeps scrolling -- faster -- while the pointer pushes up to
   * `outsideMargin` beyond it, as long as the pointer is still level with
   * the container on the other axis and not inside some unrelated scroll
   * region: a sibling's edge is that sibling's business, whereas pushing from
   * inside an ancestor is fine (the slot header above a job list sits inside
   * the board).
   * @param {Object} containerInfo - Container info object with element and capabilities
   * @param {{x: number, y: number}} pointer - The pointer position
   * @param {HTMLElement[]} pointerContainers - The scroll containers the pointer is inside of
   * @returns {Object|null} Scroll direction info or null
   */
  calculateScrollDirection(containerInfo, pointer, pointerContainers = []) {
    const { element, rect, canScrollX, canScrollY } = containerInfo;
    const threshold = this.scrollThreshold;
    const margin = this.outsideMargin;
    const levelX = pointer.x >= rect.left && pointer.x <= rect.right;
    const levelY = pointer.y >= rect.top && pointer.y <= rect.bottom;
    const pushBlocked = pointerContainers.some(other => other !== element && !other.contains(element));

    // `depth` grows from 0 at the inner edge of the zone to `threshold` on
    // the edge itself and beyond that while pushing past it.
    const edges = [
      { name: 'up', canScroll: canScrollY, level: levelX, depth: threshold - (pointer.y - rect.top) },
      { name: 'down', canScroll: canScrollY, level: levelX, depth: threshold - (rect.bottom - pointer.y) },
      { name: 'left', canScroll: canScrollX, level: levelY, depth: threshold - (pointer.x - rect.left) },
      { name: 'right', canScroll: canScrollX, level: levelY, depth: threshold - (rect.right - pointer.x) }
    ];

    const directions = {};
    for (const edge of edges) {
      if (!edge.canScroll || !edge.level) continue;
      if (edge.depth <= 0 || edge.depth > threshold + margin) continue;

      const outside = Math.max(0, edge.depth - threshold);
      if (outside > 0 && pushBlocked) continue;

      // Eases in across the edge zone, then climbs while pushing past the edge.
      const intensity = outside > 0
        ? 3 + 5 * (outside / margin)
        : 3 * Math.pow(edge.depth / threshold, 2);

      directions[edge.name] = { ghostOutside: 0, mouseOutside: outside, intensity };
    }

    return Object.keys(directions).length > 0 ? directions : null;
  }

  /**
   * Start auto-scrolling for a container in specific directions
   * @param {Object} containerInfo - Container info object
   * @param {Object} directions - Direction object with scroll info
   */
  startScroll(containerInfo, directions) {
    const { element: container, isPage } = containerInfo;
    const containerId = this.getContainerId(container);

    // Safety check - don't start if drag isn't active
    if (!this.controller.isDragging) {
      this.log("Not starting scroll - drag not active", { containerId });
      return;
    }

    // Ensure scroll snap is disabled for this container
    this.disableScrollSnapForContainer(container, isPage);

    // If already scrolling for this container, update the directions but don't create new interval
    if (this.activeScrolls.has(container)) {
      this.log("Container already scrolling, updating directions", {
        containerId,
        newDirections: Object.keys(directions)
      });
      // Store updated directions for this container
      container._scrollDirections = directions;
      return;
    }

    this.log("Starting scroll", {
      containerId,
      directions: Object.keys(directions),
      intensities: Object.fromEntries(
        Object.entries(directions).map(([dir, info]) => [dir, info.intensity.toFixed(2)])
      ),
      isPage,
      intervalMs: this.scrollInterval
    });

    // Store directions on container for access in performScroll
    container._scrollDirections = directions;


    // Perform initial scroll immediately
    this.performScroll(containerInfo, directions);

    // Start interval for this container (when mouse doesn't move)
    const intervalId = setInterval(() => {
      this.performScroll(containerInfo, container._scrollDirections || directions);
    }, this.scrollInterval);

    this.activeScrolls.set(container, intervalId);

    this.log("Scroll interval started", {
      containerId,
      intervalId,
      totalActiveScrolls: this.activeScrolls.size
    });
  }

  /**
   * Stop auto-scrolling for a container
   * @param {Object} containerInfo - Container info object
   */
  stopScroll(containerInfo) {
    const { element: container } = containerInfo;
    const intervalId = this.activeScrolls.get(container);

    if (intervalId) {
      clearInterval(intervalId);
      this.activeScrolls.delete(container);

      // Clean up stored directions
      delete container._scrollDirections;

      const containerId = this.getContainerId(container);
      this.log("Stopped scroll", {
        containerId,
        intervalId,
        remainingActiveScrolls: this.activeScrolls.size
      });
    }
  }

  /**
   * Perform the actual scrolling for a container
   * @param {Object} containerInfo - Container info object
   * @param {Object} directions - Direction object with scroll info
   */
  performScroll(containerInfo, directions) {
    // Safety check - stop scrolling if drag is no longer active
    if (!this.controller.isDragging) {
      this.log("performScroll called but drag not active, stopping all scrolls");
      this.cleanup();
      return;
    }

    const { element: container, isPage } = containerInfo;

    // Check if container still exists in DOM
    if (!isPage && !document.contains(container)) {
      this.log("Container no longer in DOM, stopping scroll", {
        containerId: this.getContainerId(container)
      });
      this.stopScroll(containerInfo);
      return;
    }

    for (const [direction, info] of Object.entries(directions)) {
      const scrollAmount = Math.min(
        this.scrollSpeed * info.intensity,
        this.maxScrollSpeed
      );

      // Check scroll boundaries before scrolling
      if (this.canScrollInDirection(container, direction, isPage)) {
        let actualScrollX = 0;
        let actualScrollY = 0;

        if (isPage) {
          const beforeScrollX = window.scrollX;
          const beforeScrollY = window.scrollY;

          // Scroll the page
          switch (direction) {
            case 'up':
              window.scrollBy(0, -scrollAmount);
              break;
            case 'down':
              window.scrollBy(0, scrollAmount);
              break;
            case 'left':
              window.scrollBy(-scrollAmount, 0);
              break;
            case 'right':
              window.scrollBy(scrollAmount, 0);
              break;
          }

          actualScrollX = window.scrollX - beforeScrollX;
          actualScrollY = window.scrollY - beforeScrollY;
        } else {
          const beforeScrollLeft = container.scrollLeft;
          const beforeScrollTop = container.scrollTop;

          // Scroll the element
          switch (direction) {
            case 'up':
              container.scrollTop = Math.max(0, container.scrollTop - scrollAmount);
              break;
            case 'down':
              container.scrollTop = Math.min(
                container.scrollHeight - container.clientHeight,
                container.scrollTop + scrollAmount
              );
              break;
            case 'left':
              container.scrollLeft = Math.max(0, container.scrollLeft - scrollAmount);
              break;
            case 'right':
              container.scrollLeft = Math.min(
                container.scrollWidth - container.clientWidth,
                container.scrollLeft + scrollAmount
              );
              break;
          }

          actualScrollX = container.scrollLeft - beforeScrollLeft;
          actualScrollY = container.scrollTop - beforeScrollTop;
        }

        // Log actual scroll deltas
        this.log("Performed scroll", {
          containerId: this.getContainerId(container),
          direction,
          requestedAmount: scrollAmount.toFixed(2),
          actualDeltaX: actualScrollX.toFixed(2),
          actualDeltaY: actualScrollY.toFixed(2),
          intensity: info.intensity.toFixed(2),
          isPage
        });

        // Log if no scrolling occurred (hit boundary)
        if (actualScrollX === 0 && actualScrollY === 0) {
          this.log("Hit boundary - no scroll", {
            containerId: this.getContainerId(container),
            direction
          });
        }
      }
    }
  }

  /**
   * Check if container can scroll in the given direction
   * @param {HTMLElement} container - The container element
   * @param {string} direction - The scroll direction
   * @param {boolean} isPage - Whether this is page-level scrolling
   * @returns {boolean} Whether scrolling is possible
   */
  canScrollInDirection(container, direction, isPage) {
    if (isPage) {
      switch (direction) {
        case 'up':
          return window.scrollY > 0;
        case 'down':
          return window.scrollY < (document.documentElement.scrollHeight - window.innerHeight);
        case 'left':
          return window.scrollX > 0;
        case 'right':
          return window.scrollX < (document.documentElement.scrollWidth - window.innerWidth);
      }
    } else {
      switch (direction) {
        case 'up':
          return container.scrollTop > 0;
        case 'down':
          return container.scrollTop < (container.scrollHeight - container.clientHeight);
        case 'left':
          return container.scrollLeft > 0;
        case 'right':
          return container.scrollLeft < (container.scrollWidth - container.clientWidth);
      }
    }
    return false;
  }

  /**
   * Get a unique identifier for a container
   * @param {HTMLElement} container - The container element
   * @returns {string} Unique identifier
   */
  getContainerId(container) {
    return container === document.documentElement ?
      'page' :
      (container.id || container.tagName + '-' + Array.from(container.parentNode.children).indexOf(container));
  }

  /**
   * Calculate overlap area between two rectangles
   * @param {DOMRect} rect1 - First rectangle
   * @param {Object} rect2 - Second rectangle with left, top, right, bottom properties
   * @returns {number} Overlap area in pixels
   */
  calculateRectOverlap(rect1, rect2) {
    const overlapX = Math.min(rect1.right, rect2.right) - Math.max(rect1.left, rect2.left);
    const overlapY = Math.min(rect1.bottom, rect2.bottom) - Math.max(rect1.top, rect2.top);

    if (overlapX > 0 && overlapY > 0) {
      return overlapX * overlapY;
    }

    return 0;
  }

  /**
   * Check if element can scroll horizontally
   * @param {HTMLElement} element - Element to check
   * @param {CSSStyleDeclaration} style - Computed style of element
   * @returns {boolean} True if element can scroll horizontally
   */
  canScrollHorizontally(element, style) {
    const overflowX = style.overflowX;
    if (overflowX === 'hidden' || overflowX === 'visible') return false;

    // Check if element actually has scrollable content
    return element.scrollWidth > element.clientWidth;
  }

  /**
   * Check if element can scroll vertically
   * @param {HTMLElement} element - Element to check
   * @param {CSSStyleDeclaration} style - Computed style of element
   * @returns {boolean} True if element can scroll vertically
   */
  canScrollVertically(element, style) {
    const overflowY = style.overflowY;
    if (overflowY === 'hidden' || overflowY === 'visible') return false;

    // Check if element actually has scrollable content
    return element.scrollHeight > element.clientHeight;
  }

  /**
   * Refresh the cache of scrollable containers
   */
  refreshContainerCache() {
    this.log("Refreshing container cache");
    this.containerCache.clear();

    // Find potentially scrollable elements more efficiently
    // Use a more targeted approach to avoid performance issues
    const potentialScrollContainers = [
      ...document.querySelectorAll('[style*="overflow"]'),
      ...document.querySelectorAll('[class*="scroll"]'),
      ...document.querySelectorAll('[class*="auto"]'),
      // Tailwind overflow classes (combined for better performance)
      ...document.querySelectorAll('.overflow-auto, .overflow-scroll, .overflow-x-auto, .overflow-x-scroll, .overflow-y-auto, .overflow-y-scroll'),
      // Additional Tailwind classes that often indicate scrollable content
      ...document.querySelectorAll('.max-h-screen, .max-h-full, .h-screen, .h-full, .min-h-screen, .min-h-full'),
      ...document.querySelectorAll('.max-w-screen, .max-w-full, .w-screen, .w-full, .min-w-screen, .min-w-full'),
      // Common container elements
      ...document.querySelectorAll('div, section, article, main, aside, nav, ul, ol, table, tbody, thead, tfoot'),
    ];

    // Use Set to avoid duplicates and convert to array
    const uniqueElements = [...new Set(potentialScrollContainers)];

    for (const element of uniqueElements) {
      // Get computed style only for elements that passed initial filters
      const style = window.getComputedStyle(element);
      const canScrollX = this.canScrollHorizontally(element, style);
      const canScrollY = this.canScrollVertically(element, style);

      if (canScrollX || canScrollY) {
        this.containerCache.set(element, {
          canScrollX: canScrollX,
          canScrollY: canScrollY
        });
      }
    }

    this.log("Container cache refreshed", {
      count: this.containerCache.size,
      scrollableX: Array.from(this.containerCache.values()).filter(c => c.canScrollX).length,
      scrollableY: Array.from(this.containerCache.values()).filter(c => c.canScrollY).length,
      both: Array.from(this.containerCache.values()).filter(c => c.canScrollX && c.canScrollY).length
    });
  }

  /**
   * Disable scroll snap for all scrollable containers
   */
  disableScrollSnap() {
    this.log("Disabling scroll snap for all containers");

    // Disable for page-level scrolling
    const documentElement = document.documentElement;
    const body = document.body;

    [documentElement, body].forEach(element => {
      this.disableScrollSnapForContainer(element, true);
    });

    // Disable for cached scrollable containers
    for (const [container] of this.containerCache) {
      this.disableScrollSnapForContainer(container, false);
    }
  }

  /**
   * Disable scroll snap for a specific container
   * @param {HTMLElement} container - The container
   * @param {boolean} isPage - Whether this is page-level scrolling
   */
  disableScrollSnapForContainer(container, isPage) {
    if (!this.originalScrollSnap.has(container)) {
      const style = window.getComputedStyle(container);
      const originalValues = {};
      let hasScrollSnap = false;

      // Check all scroll snap properties
      this.scrollSnapProperties.forEach(prop => {
        const value = style[prop];
        if (value && value !== 'none' && value !== 'normal') {
          originalValues[prop] = value;
          hasScrollSnap = true;
        }
      });

      if (hasScrollSnap) {
        this.originalScrollSnap.set(container, originalValues);

        // Reset all scroll snap properties
        container.style.scrollSnapType = 'none';
        container.style.scrollSnapAlign = 'none';
        container.style.scrollSnapStop = 'normal';

        this.log("Disabled scroll snap for container", {
          containerId: this.getContainerId(container),
          originalValues,
          isPage
        });
      }
    }
  }

  /**
   * Restore scroll snap for all containers
   */
  restoreScrollSnap() {
    this.log("Restoring scroll snap", {
      containerCount: this.originalScrollSnap.size
    });

    for (const [element, originalValues] of this.originalScrollSnap) {
      // Restore all scroll snap properties
      this.scrollSnapProperties.forEach(prop => {
        if (originalValues[prop]) {
          element.style[prop] = originalValues[prop];
        } else {
          element.style[prop] = '';
        }
      });

      this.log("Restored scroll snap for container", {
        containerId: this.getContainerId(element),
        restoredValues: originalValues
      });
    }

    this.originalScrollSnap.clear();
  }

  /**
   * Clean up all active scrolls and references
   */
  cleanup() {
    this.armedContainers = null; // re-arm per drag (see onDragMove)
    this.log("Cleaning up AutoScrollManager", {
      controllerId: this.controller.controllerId,
      isDragging: this.controller.isDragging,
      activeScrollCount: this.activeScrolls.size,
      hasInterval: !!this.continuousScrollInterval,
    });

    // Stop all active scrolls with detailed logging
    for (const [container, intervalId] of this.activeScrolls) {
      this.log("Clearing interval for container", {
        containerId: this.getContainerId(container),
        intervalId
      });
      clearInterval(intervalId);
    }

    this.activeScrolls.clear();
    this.scrollableContainers.clear();
    this.containerCache.clear();
    this.lastCacheTime = 0;
    this.dragStartTime = 0;

    // Restore scroll snap
    this.restoreScrollSnap();

    this.log("AutoScrollManager cleanup complete");
  }
}

export default class DndController extends Controller {
  static values = {
    resourceName: String,
    paramName: {
      type: String,
      default: "position",
    },
    responseKind: {
      type: String,
      default: "html",
    },
    handle: String,
    group: String,
    animation: Number,
    contexts: Array,
    reorderPath: String,
    debug: {
      type: Boolean,
      default: false,
    },
  };

  // Helper method to check if an element has exactly the "dnd" controller
  static hasDndController(element) {
    const controllers = element.dataset?.controller || "";
    return controllers.split(" ").includes("dnd");
  }

  // Helper method to find closest element with dnd controller
  static findClosestDndController(element) {
    // Use CSS selector for fast initial query, then verify exact match
    const candidate = element?.closest('[data-controller~="dnd"]');
    if (candidate && DndController.hasDndController(candidate)) {
      return candidate;
    }
    return null;
  }

  // Helper method to query all elements with dnd controller
  static queryDndControllers(root = document) {
    // Use CSS selector for fast query, then filter for exact matches
    return Array.from(root.querySelectorAll('[data-controller~="dnd"]')).filter(
      (el) => DndController.hasDndController(el),
    );
  }

  initialize() {
    this.dropHandled = false;
    this.lastPreviewUpdate = 0;
    this.lastPointerY = null;
    this.lastPreviewPosition = null; // Track last preview position
    this.previewUpdateThreshold = 32; // Minimum pixels moved before updating preview
    this.ghostManager = null;
    this.previewManager = null;
    this.dropzoneManager = null;
    this.originalManager = null; // Will be created in dragStart for the specific element
    this.dropStatusManager = null;
    this.autoScrollManager = null;
    this.isDragging = false; // Track if this controller is currently dragging

    // Generate unique ID for this controller instance
    this.controllerId = Math.random().toString(36).substr(2, 9);
  }

  getElementHierarchy(element) {
    if (!element) return "";

    const hierarchy = [];
    let current = element;

    // Walk up the DOM tree until we hit the body
    while (current && current !== document.body) {
      const id = current.id || current.dataset?.dndControllerId;
      if (id) {
        hierarchy.unshift(id);
      }
      current = current.parentElement;
    }

    return hierarchy.join(">");
  }

  debug(...args) {
    if (this.debugValue) {
      const timestamp = performance.now().toFixed(1); // Timestamp in milliseconds
      // Add controller context to debug messages
      const context = {
        controllerId: this.controllerId,
        group: this.element.dataset.dndGroupValue,
        parentController: DndController.findClosestDndController(
          this.element.parentElement,
        )?.dataset.dndGroupValue,
        elementType: this.element.tagName,
        elementClasses: this.element.className,
        originalParentId: this.originalParent?.dataset?.dndControllerId,
      };
      const hierarchy = this.getElementHierarchy(this.element);
      console.log(
        `[${timestamp}ms] DND[${hierarchy}]:`,
        ...args,
        "\nContext:",
        context,
      );
    }
  }

  connect() {
    // Add controller ID to element for easier tracking
    this.element.dataset.dndControllerId = this.controllerId;

    if (this.shouldDeferInteractions()) {
      this.observeForInteractionSetup();
      return;
    }

    this.activateInteractions();
  }

  shouldDeferInteractions() {
    return this.element.closest('[data-canvas-defer-dnd="true"]') != null;
  }

  observeForInteractionSetup() {
    if (!("IntersectionObserver" in window)) {
      this.activateInteractions();
      return;
    }

    this.interactionObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      this.interactionObserver.disconnect();
      this.interactionObserver = null;
      this.activateInteractions();
    }, { rootMargin: "400px" });
    this.interactionObserver.observe(this.element);
  }

  activateInteractions() {
    if (this.interactionsReady) return;
    this.interactionsReady = true;
    this.element.dataset.dndInteractionsReady = "true";

    this.ghostManager = new GhostManager(this);
    this.previewManager = new PreviewManager(this);
    this.dropzoneManager = new DropzoneManager(this);
    this.dropStatusManager = new DropStatusManager(this);
    this.autoScrollManager = new AutoScrollManager(this);

    this.debug("Controller CONNECTED", {
      controllerId: this.controllerId,
      elementId: this.element.id,
      elementClass: this.element.className,
      isDragging: this.isDragging
    });

    // Bind 'this' for methods called asynchronously or as callbacks
    this._finalizeDropState = this._finalizeDropState.bind(this);
    this._performDropAction = this._performDropAction.bind(this);
    this.updatePosition = this.updatePosition.bind(this); // Bind updatePosition as well
    this.handleDrop = this.handleDrop.bind(this); // handleDrop uses 'this' extensively
    this.debug = this.debug.bind(this); // Explicitly bind debug method

    // Log controller hierarchy on connect
    this.debug("Controller connected", {
      hasParentController: !!DndController.findClosestDndController(
        this.element.parentElement,
      ),
      hasChildControllers:
        DndController.queryDndControllers(this.element).length > 0,
      elementContent: this.element.textContent?.trim().slice(0, 50),
    });

    // Ensure the element has an ID for the selector
    if (!this.element.id) {
      this.element.id = `dnd-${this.controllerId}`;
    }

    this.element.style.userSelect = "none";

    // Setup dropzone first so it's ready for draggables
    this.setupDropzone();
    this.setupDraggable();

    // Handle ESC key to cancel drag
    this.escapeHandler = this.handleEscape.bind(this);
    document.addEventListener("keydown", this.escapeHandler);

    // Handle mouse leaving window
    this.mouseLeaveHandler = this.handleMouseLeave.bind(this);
    window.addEventListener("mouseleave", this.mouseLeaveHandler);
  }

  disconnect() {
    this.interactionObserver?.disconnect();
    this.interactionObserver = null;

    if (!this.interactionsReady) return;

    this.debug("Controller DISCONNECTING", {
      controllerId: this.controllerId,
      isDragging: this.isDragging,
      hasAutoScrollInterval: !!this.autoScrollManager?.continuousScrollInterval
    });

    if (this.interactable) {
      this.interactable.unset();
      this.interactable = undefined;
    }
    if (this.dropzoneInteractable) {
      this.dropzoneInteractable.unset();
      this.dropzoneInteractable = undefined;
    }
    document.removeEventListener("keydown", this.escapeHandler);
    window.removeEventListener("mouseleave", this.mouseLeaveHandler);
    this.cleanupDrag();
    delete this.element.dataset.dndInteractionsReady;
    this.interactionsReady = false;
  }

  setupDraggable() {
    if (this.interactable) {
      this.interactable.unset();
    }

    // Get handle from the closest dnd controller
    const handleValue = this.element.dataset.dndHandleValue;
    this.debug("Setting up draggable with:", {
      handleValue,
      elementDataset: this.element.dataset,
      hasHandleAttribute: this.element.hasAttribute("data-dnd-handle-value"),
    });

    const options = {
      autoScroll: false,
      // dont restrict to parent
      listeners: {
        start: this.dragStart.bind(this),
        move: this.dragMove.bind(this),
        end: this.dragEnd.bind(this)
      },
      cursorChaining: "initial",
      maxPerElement: 1,
      ignoreFrom: ".undraggable"
    };

    // Add hold delay for touch devices
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      options.hold = 500;
    }

    // Configure handle restrictions if handle is specified
    if (handleValue && handleValue.length > 0) {
      this.debug("Setting up handle mode with:", {
        handleValue,
        allowFrom: `.${handleValue}`,
      });
      options.allowFrom = `.${handleValue}`;
    } else {
      this.debug("Setting up no-handle mode");
    }

    // Make all direct children draggable using a selector
    this.interactable = interact(`#${this.element.id} > *`).draggable(options);
  }

  dragStart(event) {
    let target = event.target

    if (this.handleValue) {
      // Find the draggable child element within a dnd controller
      let current = event.target;
      while (current && current !== document.body) {
        const parent = current.parentElement;
        if (parent && DndController.hasDndController(parent)) {
          target = current;
          break;
        }
        current = parent;
      }
    }

    if (!target) {
      this.debug("dragStart aborted - no valid target");
      return;
    }

    // Check if there's already a drag in progress by looking for a ghost element
    const existingGhost = document.querySelector(".dnd-ghost");
    if (existingGhost) {
      this.debug("dragStart ignored - another drag is active", {
        existingGhost: existingGhost.textContent?.trim(),
      });
      return;
    }

    // --- Check for and clear lingering status pill/timeout --- START ---
    const lingeringPill = target.querySelector(".dnd-drop-status-pill");
    if (lingeringPill) {
      this.debug(
        "Found lingering status pill on target, removing it immediately.",
      );
      lingeringPill.remove();
    }

    // Also clear any associated timeout stored on the target element itself
    const lingeringTimeoutId = target.dataset.dndStatusTimeoutId;
    if (lingeringTimeoutId) {
      this.debug("Clearing lingering status timeout.", {
        timeoutId: lingeringTimeoutId,
      });
      clearTimeout(parseInt(lingeringTimeoutId));
      delete target.dataset.dndStatusTimeoutId; // Remove the attribute
    }
    // --- Check for and clear lingering status pill/timeout --- END ---

    // --- Initialize managers for this drag ---
    this.originalManager = new OriginalManager(target, this); // Pass controller instance
    this.originalManager.createCleanClone(); // Create clone BEFORE modifying original
    this.originalManager.storeOriginalPosition(); // Store original parent/index
    this.originalManager.markAsSource(); // Add .dnd-source class
    // dropStatusManager is already initialized, will be attached in handleDrop

    // --- Detect and store draggable types --- START ---
    const detectedTypes = [];
    // Convert camelCase dataset key prefix to kebab-case type string
    const camelToKebab = (str) =>
      str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);

    for (const key in target.dataset) {
      // Check if the key ends with 'Id' (case-sensitive) and is not the simple 'id' key
      if (key !== "id" && key.endsWith("Id")) {
        const prefix = key.slice(0, -2); // Remove 'Id' suffix
        const typeString = camelToKebab(prefix);
        if (!detectedTypes.includes(typeString)) {
          // Avoid duplicates
          detectedTypes.push(typeString);
        }
      }
    }

    if (detectedTypes.length > 0) {
      target.dataset.dndDetectedTypes = JSON.stringify(detectedTypes);
      this.debug("Detected types (convention-based):", detectedTypes, {
        element: target,
      });
    } else {
      this.debug("No specific types detected via convention", {
        element: target,
      });
    }
    // --- Detect and store draggable types --- END ---

    // Store the original parent controller for reference
    this.originalParent = DndController.findClosestDndController(target);

    // Store the original index in the parent (Handled by OriginalManager now)
    // target.originalIndex = Array.from(target.parentElement.children).indexOf(target)

    // Mark this controller as the one handling the drag
    this.isDragging = true;

    // Add modern touchmove prevention for touch devices
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      // Add body class for CSS-based prevention
      document.body.classList.add('dnd-drag-active');

      // Aggressive touch prevention handler
      this.touchMoveHandler = (e) => {
        if (this.isDragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      };

      // Use capture phase for earlier interception
      document.addEventListener('touchmove', this.touchMoveHandler, {
        passive: false,
        capture: true
      });
    }

    // Log drag hierarchy
    const targetController = DndController.findClosestDndController(target);
    const dragHierarchy = {
      targetController: targetController?.dataset.dndGroupValue,
      parentController: DndController.findClosestDndController(
        targetController?.parentElement,
      )?.dataset.dndGroupValue,
      targetContent: target.textContent?.trim().slice(0, 50),
      targetClasses: target.className,
      isNestedDrag: !!DndController.findClosestDndController(
        targetController?.parentElement,
      ),
      hierarchy: this.getElementHierarchy(target),
    };

    this.debug("dragStart", {
      target: target.textContent?.trim(),
      handle: this.handleValue,
      eventTarget: event.target.textContent?.trim(),
      rect: target.getBoundingClientRect(),
      hierarchy: dragHierarchy,
      isDragging: this.isDragging,
    });

    this.dropHandled = false;

    // Store original dimensions
    const rect = target.getBoundingClientRect();
    const style = window.getComputedStyle(target);

    // Create ghost using GhostManager
    const ghost = this.ghostManager.createGhost(target, rect, style);

    // Add source class to original (Handled by OriginalManager now)
    // target.classList.add('dnd-source')

    // Store references
    target.ghost = ghost;
    ghost.source = target;

    // Store initial coordinates and initial transform
    this.initialX = event.clientX - rect.left;
    this.initialY = event.clientY - rect.top;
    this.initialLeft = rect.left;
    this.initialTop = rect.top;
  }

  dragMove(event) {
    this.debug("dragMove called", {
      controllerId: this.controllerId,
      isDragging: this.isDragging,
      hasGhost: !!event.target?.ghost,
      eventTargetText: event.target?.textContent?.trim().slice(0, 30)
    });

    const target = event.target;
    const ghost = target.ghost;
    if (!ghost) {
      this.debug("dragMove aborted - no ghost found", {
        eventTarget: target.textContent?.trim().slice(0, 50),
        hasGhost: !!ghost,
        targetController:
          DndController.findClosestDndController(target)?.dataset.dndGroupValue,
      });
      return;
    }

    // Calculate new position relative to initial position
    const deltaX = event.clientX - this.initialX;
    const deltaY = event.clientY - this.initialY;
    const newX = this.initialLeft + (deltaX - this.initialLeft);
    const newY = this.initialTop + (deltaY - this.initialTop);

    // Move ghost using GhostManager
    this.ghostManager.moveGhost(ghost, newX, newY);

    // Find best matching dropzone using the controller's group value
    const sourceControllerElement =
      DndController.findClosestDndController(target);
    const sourceGroup = sourceControllerElement
      ? sourceControllerElement.getAttribute("data-dnd-group-value") ||
        this.groupValue
      : null;
    const bestDropzoneInstance = this.dropzoneManager.findIntersectingDropzones(
      ghost,
      this,
      sourceGroup,
    );

    let instantHideTriggeredInThisCycle = false;
    // Update OriginalManager hover state
    if (this.originalManager) {
      if (bestDropzoneInstance && bestDropzoneInstance.isAccepting) {
        const matchedCtx = bestDropzoneInstance.matchedContext;
        const isSameTargetCtx = !!matchedCtx?.sameTargetOnly;
        this.originalManager.updateHoverState(matchedCtx, isSameTargetCtx);

        // Check if this updateHoverState call resulted in an instant hide
        if (
          matchedCtx?.removeOriginal === "onbeforedrop" &&
          isSameTargetCtx &&
          this.originalManager.isPreviewingRemoval
        ) {
          instantHideTriggeredInThisCycle = true;
          this.debug("Instant hide was triggered in this dragMove cycle.");
        }
      } else {
        this.originalManager.updateHoverState(null); // No valid hover or ghost moved away
      }
    }

    // Update pill indicator, passing the bestDropzone object which contains the matchedContext
    this.ghostManager.updatePill(
      ghost,
      bestDropzoneInstance,
      sourceControllerElement,
    );

    // Handle auto-scroll during drag move - only for the dragging controller
    if (this.isDragging) {
      this.autoScrollManager.onDragMove(ghost, event);
    }

    // Only update preview if we have a best dropzone and it's accepting
    if (bestDropzoneInstance && bestDropzoneInstance.isAccepting) {
      const now = Date.now();
      const pointer = { x: event.clientX, y: event.clientY };

      // Only update if this is the current active dropzone
      if (bestDropzoneInstance === this.dropzoneManager.getCurrentDropzone()) {
        if (instantHideTriggeredInThisCycle) {
          // Defer preview update slightly to allow display:none on original to take effect first
          Promise.resolve()
            .then(() => {
              // Re-check conditions as state might have changed during microtask
              if (
                this.isDragging &&
                this.dropzoneManager.getCurrentDropzone() ===
                  bestDropzoneInstance &&
                bestDropzoneInstance.isAccepting
              ) {
                this.debug(
                  "Updating preview via microtask after instant hide.",
                );
                bestDropzoneInstance.updatePreview(target, pointer);
              } else {
                this.debug("Skipped microtask preview update, state changed.");
              }
            })
            .catch((e) =>
              console.error("Microtask for preview update failed:", e),
            );
        } else {
          bestDropzoneInstance.updatePreview(target, pointer);
        }
      }
    }
  }

  setupDropzone() {
    if (this.dropzoneInteractable) {
      this.dropzoneInteractable.unset();
    }

    // We no longer need the interact.js dropzone since we're handling it ourselves
    // Just store a reference to mark this as a dropzone
    this.element.dataset.isDndDropzone = "true";
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      // Only handle escape if this controller is actually dragging
      if (!this.isDragging) {
        return;
      }

      this.debug("handleEscape: Drag cancelled by user");
      this.dropHandled = true; // Prevent any pending drop operations

      // Stop all auto-scrolling immediately - only if this controller is dragging
      if (this.isDragging && this.autoScrollManager) {
        this.autoScrollManager.cleanup();
      }

      if (this.originalManager) {
        this.originalManager.updateHoverState(null); // Ensure original element is restored
        // The originalManager.cleanup() call below will do further restoration if necessary
      }

      // Remove all drag-related elements immediately
      this.ghostManager.cleanup();
      this.previewManager.cleanup();
      if (this.dropStatusManager) this.dropStatusManager.cleanup();
      if (this.originalManager) this.originalManager.cleanup();
      this.dropzoneManager.cleanup();

      // Reset the drag operation in interact
      if (this.interactable) {
        this.interactable.options.drag.enabled = false;
        this.interactable.options.drag.enabled = true;
      }
      this.isDragging = false; // Ensure dragging state is reset
    }
  }

  cleanupDrag() {
    this.debug("cleanupDrag called", {
      controllerId: this.controllerId,
      isDragging: this.isDragging,
      dropHandled: this.dropHandled,
      hasGhost: !!document.querySelector(".dnd-ghost"),
      calledFrom: new Error().stack.split('\n')[2].trim(),
    });

    // Remove touch event listeners with proper options
    if (this.touchMoveHandler) {
      document.removeEventListener('touchmove', this.touchMoveHandler, {
        passive: false,
        capture: true
      });
      this.touchMoveHandler = null;

      // Remove body class
      document.body.classList.remove('dnd-drag-active');
    }

    // If the drag is active and not yet handled (e.g. cancelled mid-flight, or ended outside a dropzone)
    // ensure the originalManager correctly restores the element if it was in a preview-removal state.
    if (this.isDragging && !this.dropHandled && this.originalManager) {
      this.originalManager.updateHoverState(null); // This will trigger restore if isPreviewingRemoval was true
    }

    // Only the controller actually managing the drag should perform the full cleanup.
    if (this.isDragging) {
      if (this.dropHandled) {
        this.debug(
          "cleanupDrag on dragging controller - drop already handled, assuming finalize handled cleanup",
        );
        this.isDragging = false;
        return;
      }

      // Don't cleanup ghost if it's in the middle of dropping animation
      const ghost = document.querySelector(".dnd-ghost"); // Check global ghost
      if (ghost && ghost.classList.contains("dropping")) {
        this.debug(
          "cleanupDrag on dragging controller - ghost is animating, skipping cleanup",
        );
        return;
      }

      this.debug(
        "cleanupDrag on dragging controller - performing full cleanup via managers",
      );
      this.ghostManager.cleanup();
      this.previewManager.cleanup();
      this.dropzoneManager.cleanup();
      if (this.dropStatusManager) this.dropStatusManager.cleanup();
      if (this.originalManager) this.originalManager.cleanup();
    } else {
      this.debug(
        "cleanupDrag on non-dragging controller - skipping DOM cleanup",
      );
    }
    this.isDragging = false;
    this.originalManager = null; // Clear manager reference
  }

  handleDrop(event) {
    this.debug("handleDrop", {
      draggable: event.relatedTarget.textContent?.trim(),
      dropzone: event.target.textContent?.trim(),
    });

    if (this.dropHandled) {
      this.debug("handleDrop - already handled");
      return;
    }
    this.dropHandled = true;

    const draggable = event.relatedTarget;
    const bestDropzone = this.dropzoneManager.getCurrentDropzone();
    const ghost = draggable.ghost;

    if (bestDropzone && ghost) {
      const sourceList = DndController.findClosestDndController(draggable);
      const preview = bestDropzone.getPreview();

      if (preview) {
        const pillElement = this.dropStatusManager.attachTo(preview);

        document
          .querySelectorAll(
            ".dnd-potential-dropzone, .dnd-active-dropzone, .dnd-rejected-dropzone",
          )
          .forEach((el) => {
            el.classList.remove(
              "dnd-potential-dropzone",
              "dnd-active-dropzone",
              "dnd-rejected-dropzone",
            );
          });

        const matchedContextOnDrop = bestDropzone?.matchedContext;

        if (this.originalManager) {
          const isSameTargetContextOnDrop =
            !!matchedContextOnDrop?.sameTargetOnly;
          this.originalManager.prepareForDrop(
            matchedContextOnDrop,
            isSameTargetContextOnDrop,
          ); // Pass the whole context and sameTarget flag
        } else {
          this.debug(
            "ERROR: originalManager not found in handleDrop before prepareForDrop",
          );
        }

        this.ghostManager.animateToPreview(ghost, preview, async () => {
          this.debug("Ghost animation complete, initiating drop action");

          const currentDropzone = this.dropzoneManager.getCurrentDropzone(); // Re-fetch, just in case
          const matchedContext = currentDropzone?.matchedContext;

          const isReorder = !matchedContext?.dropPath;

          if (isReorder && matchedContext) {
            this.debug("Dispatching: Reorder action");
            const isNewList = currentDropzone.element !== sourceList; // Calculate isNewList here
            await this.updatePosition(
              preview,
              isNewList,
              currentDropzone.element,
              draggable,
              pillElement,
            );
          } else if (matchedContext && matchedContext.dropPath) {
            this.debug("Dispatching: Server action (via context.dropPath)");

            // Use the hook system to build custom request config based on context
            const requestConfig = this._buildRequestConfig(
              matchedContext,
              draggable,
              preview,
            );

            const actionConfig = {
              ...requestConfig,
              responseKind: this.responseKindValue,
              previewElement: preview,
              originalElement: draggable,
              matchedContext: matchedContext, // Pass context for reference
            };
            await this._performDropAction(actionConfig);
          } else {
            this.debug("Dispatching: No valid action/context found");
            this.dropStatusManager.updateStatus("error", "No action");
            await new Promise((resolve) => setTimeout(resolve, 2200));
            this._finalizeDropState(false, draggable, preview);
          }
        });
      } else {
        this.debug("handleDrop cleanup - missing preview");
        if (!this.dropHandled) {
          // If no preview, it's like a failed drop early on.
          // Finalize with failure, originalManager will restore.
          this._finalizeDropState(false, draggable, null);
        }
      }
    } else {
      this.debug("handleDrop cleanup - missing bestDropzone or ghost");
      if (!this.dropHandled) {
        // This is likely a drag that ended outside any valid zone.
        // Finalize with failure, originalManager will restore.
        this._finalizeDropState(false, draggable, null);
      }
    }
  }

  dragEnd(event) {
    // Only handle dragEnd if this controller initiated the drag
    if (!this.isDragging) {
      this.debug("dragEnd ignored - not the initiating controller", {
        isDragging: this.isDragging,
        hasGhost: !!document.querySelector(".dnd-ghost"),
      });
      return;
    }

    // Stop all auto-scrolling immediately at drag end
    if (this.autoScrollManager) {
      this.autoScrollManager.cleanup();
    }

    this.debug("dragEnd", {
      target: event.target.textContent?.trim(),
      hasDropzone: !!event.target.dropzone,
      hasPreview: !!document.querySelector(".dnd-preview"),
      dropHandled: this.dropHandled,
      stack: new Error().stack,
      isDragging: this.isDragging,
    });

    const preview = document.querySelector(".dnd-preview");
    const dropzone = event.target.dropzone;
    const currentDropzone = this.dropzoneManager.getCurrentDropzone();

    // Only force a drop if:
    // 1. We have a preview
    // 2. We have a current dropzone that's accepting
    // 3. The drop hasn't been handled yet
    if (
      preview &&
      currentDropzone &&
      currentDropzone.isAccepting &&
      !this.dropHandled
    ) {
      this.debug("dragEnd - forcing drop event");
      this.handleDrop({
        target: currentDropzone.element,
        relatedTarget: event.target,
      });
      // Do not return here, let the flow continue to cleanup and reset isDragging
    } else if (!this.dropHandled) {
      // If drop wasn't forced and not handled, then cleanup
      this.debug("dragEnd cleanup - no valid drop or drop already handled");
      if (this.originalManager) {
        // Ensure original is restored if it was previewing removal
        this.originalManager.updateHoverState(null);
      }
      this.cleanupDrag(); // This will now respect the isDragging flag internally
    }

    // Reset dragging state for this controller if it was the one dragging
    this.isDragging = false;
  }

  handleMouseLeave(event) {
    // Only handle mouse leave if this controller is actually dragging
    if (!this.isDragging) {
      return;
    }

    // Only trigger cleanup if mouse has truly left the window (not just moving to an iframe or child element)
    if (event.relatedTarget === null) {
      // Force cleanup auto-scroll immediately - only if this controller is dragging
      if (this.isDragging && this.autoScrollManager) {
        this.autoScrollManager.cleanup();
      }

      this.cleanupDrag();

      // Also reset any active interact drag operation
      if (this.interactable) {
        this.interactable.options.drag.enabled = false;
        this.interactable.options.drag.enabled = true;
      }
    }
  }

  // Helper method for handleDrop
  async updatePosition(
    preview,
    isNewList,
    dropzone,
    originalElement,
    pillElement = null,
  ) {
    this.debug("updatePosition - Preparing for reorder", {
      reorderPath: this.reorderPathValue,
      preview: preview ? preview.textContent?.trim() : "no-preview",
      isNewList,
      dropzone: dropzone ? dropzone.textContent?.trim() : "no-dropzone",
      originalElementId:
        originalElement && originalElement.dataset
          ? originalElement.dataset.id
          : "no-original-element-id",
    });

    // let activePill = pillElement; // pillElement is now managed by _performDropAction or created if simulated

    if (!this.reorderPathValue) {
      this.debug("No reorder path, simulating success for reorder");
      if (this.originalManager) {
        // For a reorder simulation, it's always a "move"
        this.originalManager.prepareForDrop(true);
      } else {
        this.debug(
          "ERROR: originalManager not found in updatePosition simulation path",
        );
      }
      // Ensure a pill is shown for simulated success
      const tempPill = this.dropStatusManager.attachTo(preview);
      this.dropStatusManager.updateStatus("success", "Succeeded");
      this._finalizeDropState(true, originalElement, preview);
      return;
    }

    const childElements = Array.from(dropzone.children);
    this.debug(
      "updatePosition - Raw child elements:",
      childElements.map((el) => ({
        tagName: el.tagName,
        id: el.id,
        dataId: el.dataset ? el.dataset.id : "N/A",
        classList: Array.from(el.classList),
      })),
    );

    // Get all IDs in order from the dropzone
    const idsWithNaN = childElements.map((el) => {
      let parsedId = NaN;
      let source = "unknown";

      if (el === preview) {
        source = "preview (using originalElement.dataset.id)";
        if (
          originalElement &&
          originalElement.dataset &&
          originalElement.dataset.id !== undefined
        ) {
          parsedId = parseInt(originalElement.dataset.id);
        } else {
          this.debug(
            "updatePosition - preview element missing originalElement.dataset.id",
            { el, originalElement },
          );
        }
      } else if (el === originalElement) {
        source = "originalElement (moved, to be excluded explicitly)";
        parsedId = NaN;
        this.debug(
          "updatePosition - Excluding moved originalElement from ID list",
          {
            elementTag: el.tagName,
            elementIdAttr: el.id,
            elementDataId: el.dataset ? el.dataset.id : "N/A",
          },
        );
      } else if (el.dataset && el.dataset.id !== undefined) {
        source = "element.dataset.id";
        parsedId = parseInt(el.dataset.id);
      } else {
        source = "no data-id";
      }

      if (
        isNaN(parsedId) &&
        source !== "no data-id" &&
        source !== "originalElement (moved, to be excluded explicitly)"
      ) {
        this.debug(
          "updatePosition - element resulted in NaN (and was not the moved original or an element with no data-id)",
          {
            elementTag: el.tagName,
            elementIdAttr: el.id,
            elementDataId: el.dataset ? el.dataset.id : "N/A",
            source,
            parsedIdInput:
              source === "preview (using originalElement.dataset.id)"
                ? originalElement?.dataset?.id
                : el.dataset?.id,
          },
        );
      }
      return parsedId;
    });

    this.debug("updatePosition - IDs before filtering NaN:", idsWithNaN);
    const idsInOrder = idsWithNaN.filter((id) => !isNaN(id));

    this.debug("updatePosition - Calling _performDropAction for reorder", {
      url: this.reorderPathValue,
      idsInOrder,
      isNewList,
    });

    // Construct config and call the generic action handler
    const actionConfig = {
      path: this.reorderPathValue,
      body: JSON.stringify({ ids_in_order: idsInOrder }),
      responseKind: this.responseKindValue,
      previewElement: preview,
      originalElement: originalElement,
      // pillElement is now implicitly handled by _performDropAction via dropStatusManager
    };
    await this._performDropAction(actionConfig);
  }

  // --- New Private Helper Methods --- START ---

  /**
   * Hook system to customize request data and method based on context
   */
  _buildRequestConfig(matchedContext, draggable, previewElement) {
    const contextName = matchedContext?.context;
    const defaultConfig = {
      method: "POST",
      path: matchedContext?.dropPath,
      body: JSON.stringify({ sourceElementData: draggable.dataset }),
      headers: {
        "Content-Type": "application/json",
      },
    };

    // Context-specific hooks
    switch (contextName) {
      case "addAssignedJobFromTemplate":
        return this._buildAddJobFromTemplateConfig(
          matchedContext,
          draggable,
          defaultConfig,
        );
      case "assignPersonToJob":
        return this._buildAssignPersonToJobConfig(
          matchedContext,
          draggable,
          defaultConfig,
        );
      case "assignPersonToSlot":
        return this._buildAssignPersonToSlotConfig(
          matchedContext,
          draggable,
          defaultConfig,
        );
      default:
        return defaultConfig;
    }
  }

  /**
   * Hook for addAssignedJobFromTemplate context
   */
  _buildAddJobFromTemplateConfig(matchedContext, draggable, defaultConfig) {
    const jobId = draggable.dataset.jobId;

    if (!jobId) {
      this.debug(
        "_buildAddJobFromTemplateConfig: No jobId found in draggable dataset",
      );
      return defaultConfig;
    }

    // Use GET with query parameters for prefilling (Rails convention)
    const url = new URL(matchedContext.dropPath, window.location.origin);
    url.searchParams.set("job_id", jobId);

    return {
      method: "GET",
      path: url.pathname + url.search,
      body: null, // GET requests don't have body
      headers: {
        Accept: "text/html, application/xhtml+xml",
      },
    };
  }

  /**
   * The `SharedPerson#<id>` token for a row representing a space person who is
   * not in the plan yet, or null for an ordinary plan person.
   */
  #sharedPersonSelection(draggable) {
    // Only `data-shared-person-id` makes a token. A drawer row also carries
    // `data-drawer-person-selection` for its own decorations and quick-add,
    // and for someone on the slot that value is an ASSIGNED-person id, which
    // the slot endpoint's resolver would misread as a plan-person id.
    if (draggable.dataset.sharedPersonId)
      return `SharedPerson#${draggable.dataset.sharedPersonId}`;

    return null;
  }

  /**
   * Hook for assignPersonToJob context
   */
  _buildAssignPersonToJobConfig(matchedContext, draggable, defaultConfig) {
    const assignedPersonId = draggable.dataset.assignedPersonId;
    const personId = draggable.dataset.personId;
    // Dragged out of the people drawer's "not in this plan yet" section. The
    // id is a SPACE person's, which means nothing to an endpoint that looks
    // ids up among plan people -- so send the selection token the server
    // resolver understands instead, and let it create the plan person.
    const selection = this.#sharedPersonSelection(draggable);

    if (!personId && !selection) {
      this.debug(
        "_buildAssignPersonToJobConfig: No personId found in draggable dataset",
      );
      return defaultConfig;
    }

    // Use GET with query parameters for prefilling (Rails convention)
    const url = new URL(
      matchedContext.dropPath + "/new",
      window.location.origin,
    );
    if (selection) {
      url.searchParams.set("selection", selection);
    } else {
      url.searchParams.set("person_id", personId);
    }

    return {
      method: "GET",
      path: url.pathname + url.search,
      body: null, // GET requests don't have body
      headers: {
        Accept: "text/html, application/xhtml+xml",
      },
    };
  }

  /**
   * Hook for assignPersonToSlot context
   */
  _buildAssignPersonToSlotConfig(matchedContext, draggable, defaultConfig) {
    // A space person rides in as a selection token. The slot endpoint already
    // runs person_id through Staffing::PlanPersonSelectionResolver, so it
    // find-or-creates the plan person with no server change needed here.
    const selection = this.#sharedPersonSelection(draggable);
    const personId = selection || draggable.dataset.personId;

    if (!personId) {
      this.debug(
        "_buildAssignPersonToSlotConfig: No personId found in draggable dataset",
      );
      return defaultConfig;
    }

    let confirmationPath;
    if (selection && matchedContext.assignmentConfirmationPath) {
      const url = new URL(matchedContext.assignmentConfirmationPath, window.location.origin);
      url.searchParams.set("shared_person_id", draggable.dataset.sharedPersonId);
      confirmationPath = url.pathname + url.search;
    }

    return {
      method: "POST",
      path: matchedContext.dropPath,
      confirmation: matchedContext.assignmentConfirmations?.[`${personId}`],
      confirmationPath,
      body: JSON.stringify({
        staffing_plans_slots_assigned_person: { person_id: personId },
        refresh_mode: "current",
        return_to: window.location.pathname + window.location.search,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    };
  }

  // The drop succeeded on the server, and a canvas refresh already landed with
  // the answer: the list we dropped into has been re-rendered underneath us.
  //
  // The refresh morphs by DOM id, and during a drag the dragged card's id
  // exists twice -- on the card itself and on the preview cloned from it. The
  // morph keeps whichever of the two it matched and detaches the other along
  // with the old list. Either way our references are stale by the time the
  // status pill has had its moment, and patching the page with them removes a
  // card the server just rendered.
  //
  // One of the two being detached is the signal: neither leaves the page
  // during a drag we are still driving ourselves.
  _dropTargetWasRefreshed(originalElement, previewElement) {
    return Boolean(
      (previewElement && !previewElement.isConnected) ||
        (originalElement && !originalElement.isConnected),
    );
  }

  // The refresh matches by id, and the preview carries the dragged card's id,
  // so the re-rendered list may well be holding the preview as the card's own
  // node. Then it is page content, not a drag artifact: let go of it, because
  // every preview a dropzone still holds is removed when the drag cleans up.
  _releasePreviewToRefresh(previewElement) {
    if (!previewElement) return;

    previewElement.classList.remove("dnd-preview");
    this.dropzoneManager.dropzones.forEach((dropzone) => {
      if (dropzone.currentPreview === previewElement) {
        dropzone.currentPreview = null;
      }
    });
  }

  _finalizeDropState(success, originalElement, previewElement) {
    this.debug("_finalizeDropState decision logic", {
      success,
      originalElementId: originalElement?.dataset.id,
      previewElementExists: !!previewElement,
      currentDOMPreview: document.querySelector(".dnd-preview"), // For debugging
    });

    if (success && this._dropTargetWasRefreshed(originalElement, previewElement)) {
      this.debug(
        "_finalizeDropState: a refresh already re-rendered the drop target, leaving the DOM to it",
        {
          previewConnected: previewElement?.isConnected,
          originalConnected: originalElement?.isConnected,
        },
      );
      if (this.dropStatusManager) this.dropStatusManager.cleanup();
      this._releasePreviewToRefresh(previewElement);
      if (this.originalManager) this.originalManager.releaseAfterRefresh();
      requestAnimationFrame(() => {
        this.dropzoneManager.cleanup();
        this.ghostManager.cleanup();
      });
      return;
    }

    if (success) {
      const cleanClone = this.originalManager?.cleanClone;
      const parentNode = previewElement?.parentNode;

      if (cleanClone && parentNode) {
        this.debug("Inserting clean clone via replaceChild", {
          parentNode,
          previewElementContent: previewElement?.textContent,
        });
        try {
          // The act of replacing the previewElement with the cleanClone effectively removes the previewElement.
          parentNode.replaceChild(cleanClone, previewElement);
          this.debug(
            "Inserted clean clone, previewElement removed by replacement.",
          );
          if (this.dropStatusManager)
            this.dropStatusManager.showStatusOnFinalElement(
              cleanClone,
              "success",
              "Succeeded",
            );
        } catch (e) {
          this.debug("ERROR inserting clean clone via replaceChild:", e);
          console.error("Error inserting clean clone via replaceChild:", e, {
            parentNode,
            cleanClone,
            previewElement,
          });
          success = false; // Treat as failure if clone insertion fails
        }
      } else {
        this.debug(
          "ERROR: Cannot insert final clone - cleanClone or previewElement.parentNode missing",
          { hasCleanClone: !!cleanClone, hasParentNode: !!parentNode },
        );
        success = false;
      }
    }

    // Handle original element based on final success state
    if (this.originalManager) {
      this.originalManager.finalizeAfterDrop(success);
    } else {
      this.debug("ERROR: originalManager not found in _finalizeDropState");
    }

    if (success) {
      const cleanClone = this.originalManager?.cleanClone;
      const cleanCloneOriginalId = this.originalManager?.cleanCloneOriginalId;
      if (
        cleanClone &&
        cleanCloneOriginalId &&
        this.originalManager?.removeOriginalMode !== "keep"
      ) {
        cleanClone.id = cleanCloneOriginalId;
      }
    }

    // If the drop was not successful, ensure any lingering preview is globally cleaned up.
    if (!success) {
      this.debug(
        "_finalizeDropState (failure path): Ensuring global preview cleanup.",
      );
      if (this.previewManager) {
        this.previewManager.cleanup(); // Global cleanup for any .dnd-preview
      }
    } else {
      // If drop was successful AND clone insertion replaced previewElement,
      // previewElement is already gone. No specific action needed for previewManager here.
      this.debug(
        "_finalizeDropState (success path): Preview assumed removed by replaceChild.",
      );
    }

    // General cleanup (ghost, dropzones)
    requestAnimationFrame(() => {
      this.debug(
        "Performing delayed general cleanup in _finalizeDropState (dropzones, ghost)",
      );
      this.dropzoneManager.cleanup(); // Cleans dropzone states and their currentPreviews if any
      this.ghostManager.cleanup();
    });
  }

  async _performDropAction(actionConfig) {
    const {
      method = "POST",
      path,
      body,
      headers = {},
      responseKind,
      previewElement,
      originalElement,
      matchedContext,
      confirmation,
      confirmationPath,
    } = actionConfig;

    if (confirmation && !window.confirm(confirmation)) {
      if (originalElement || previewElement) {
        this._finalizeDropState(false, originalElement, previewElement);
      }
      return false;
    }

    this.debug("_performDropAction initiating server request", {
      method,
      path,
      body: typeof body === "string" ? JSON.parse(body) : body,
      headers,
      context: matchedContext?.context,
    });

    // Pill status would have been set to "Performing..." by attachTo in handleDrop
    // If this is a reorder simulation without a path, that path is handled in updatePosition

    let success = false;
    try {
      if (confirmationPath) {
        const response = await get(confirmationPath, { responseKind: "json" });
        if (!response.ok) throw new Error(`Confirmation request returned ${response.statusCode}`);
        const { confirmation: deferredConfirmation } = await response.json;
        if (deferredConfirmation && !window.confirm(deferredConfirmation)) {
          if (originalElement || previewElement) {
            this._finalizeDropState(false, originalElement, previewElement);
          }
          return false;
        }
      }

      if (method === "GET") {
        // For GET requests (like form prefilling), navigate to the URL
        this.debug("Navigating to prefilled form", {
          path,
          context: matchedContext?.context,
        });
        let frame = document.querySelector('turbo-frame#modal')
        if (frame) {
          frame.src = path
        } else{
          Turbo.visit(path);
        }
        success = true; // Assume success since we're navigating away
        if (this.dropStatusManager)
          this.dropStatusManager.updateStatus("success", "Navigating...");
      } else {
        // For POST/other methods, use the Rails request helper
        const requestOptions = {
          body: body,
          responseKind: responseKind,
        };

        // Add custom headers if provided
        if (Object.keys(headers).length > 0) {
          requestOptions.headers = headers;
        }

        const response = await post(path, requestOptions);

        if (!response.ok) {
          let errorDetail = `Server returned ${response.status}`;
          // Attempt to get more error details from response if possible
          try {
            const errorData = await response.text(); // or response.json() if applicable
            errorDetail += ` - ${errorData.slice(0, 100)}`;
          } catch (parseError) {
            /* ignore if can't parse body */
          }
          throw new Error(errorDetail);
        }

        this.debug("Drop action request succeeded", {
          path,
          context: matchedContext?.context,
        });
        success = true;
        // Update status on the preview pill
        if (this.dropStatusManager)
          this.dropStatusManager.updateStatus("success", "Succeeded");
      }
    } catch (error) {
      console.error("Drop action request failed:", error);
      this.debug("Drop action request failed", {
        path,
        context: matchedContext?.context,
        error: error.message,
      });
      success = false;
      if (this.dropStatusManager)
        this.dropStatusManager.updateStatus(
          "error",
          `Failed: ${error.message.substring(0, 50)}`,
        );
    }

    if (method === "GET") {
      // A prefilled modal has not committed a move. Restore the source and
      // remove the transient drop UI so cancel leaves it available to drag.
      this._finalizeDropState(false, originalElement, previewElement);
      this.dropStatusManager?.cleanup();
      return;
    }

    this.debug("Server action complete. Status before finalize timeout:", {
      success,
      pillText: this.dropStatusManager.pillElement?.textContent,
    });

    const pillDuration = success ? 700 : 2200;
    await new Promise((resolve) => setTimeout(resolve, pillDuration));

    this._finalizeDropState(success, originalElement, previewElement);
  }

  // --- New Private Helper Methods --- END ---
}
